import {
  DateField,
  SectionTitle,
  GradientIcon,
  MoonStepper,
  Collapsible,
  CardTitleText,
  CloseButton,
  Dialog,
  DialogActions,
  DialogHeader,
  DialogIcon,
  PrimaryButton,
  Row,
  CardSubtitleText,
  CompactActionButton,
  SecondaryButton,
  InlineOptionPicker,
  TextActionButton,
  ActionBanner,
  MoonCheckboxBox,
  MoonSegmentedControl,
  CardSurface,
  cardSurface,
  MoonFormField,
  TextField,
  EmptyState,
} from "@louise/moon-ui";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  View,
  Text,
  ScrollView,
  Pressable,
  Platform,
  Image,
} from "react-native";
import * as ExpoLocation from "expo-location";
import * as DocumentPicker from "expo-document-picker";
import * as ImagePicker from "expo-image-picker";
import type { NavigationProp } from "@react-navigation/native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import {
  Briefcase,
  Building2,
  Calculator,
  Camera,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  CreditCard,
  FileCheck2,
  FileQuestion,
  FileText,
  Gift,
  Landmark,
  MapPin,
  MessageCircle,
  Minus,
  Plus,
  Receipt,
  Shield,
  SlidersHorizontal,
  Sparkles,
  UploadCloud,
  Wallet,
  RotateCcw,
  Package,
  X,
} from "lucide-react-native";
import type { LucideIcon } from "lucide-react-native";
import { useApiClient } from "@src/packages/api/louise-api";
import {
  projectDocumentProcessingRunToPrefill,
  useDocumentSourceTransfer,
} from "@src/packages/documents/ingestion";
import { resolveUploadContentType } from "@src/packages/utils/fileUpload";
import {
  useClinicQuotes,
  useCreateClinicQuote,
  useDeleteClinicQuote,
  useUpdateClinicQuote,
  type ApiClinicQuote,
  type ClinicQuoteExtraction,
} from "@src/packages/costs/hooks/useClinicQuotes";
import {
  useCreatePaymentRecord,
  useDeletePaymentRecord,
  usePaymentRecords,
  useUpdatePaymentRecord,
  type CreatePaymentRecordPayload,
} from "@src/packages/costs/hooks/usePaymentRecords";
import { useClinicComparisonStore } from "@src/packages/clinics/clinicComparisonStore";
import { useCoverage } from "@src/packages/coverage/hooks/useCoverage";
import type {
  FertilityCoverage,
  InsurancePolicy,
} from "@src/packages/coverage/hooks/useCoverage";
import { useCoverageFinancials } from "@src/packages/coverage/hooks/useCoverageFinancials";
import {
  buildEstimateScenarioSections,
  buildDetectedEstimateAreaOption,
  buildFallbackEstimateAreaOption,
  buildProfileEstimateAreaOption,
  getLineAvailability,
  getMedicationLineId,
  getUsStateName,
  getUsStateCentroid,
  hasTransferPath,
  hasMedicationEstimate,
  inferUsStateCodeFromZip,
  isLocationSpecificEstimate,
  normalizeUsStateCode,
  resolvePlaceEstimateArea,
  US_CITY_ESTIMATE_AREA_OPTIONS,
  type CostAvailabilityPolicy,
  type CostRestrictionReason,
  type EstimateLineId,
  type PlaceEstimateInput,
  type PlaceEstimateResolution,
  type EstimateAreaOption,
  type EstimateLineStatus,
  type EstimateScenarioLine,
  type EstimateScenarioSection,
} from "@src/packages/costs/estimates";
import {
  getPlaceDetails,
  extractPlaceArea,
} from "@src/packages/clinics/utils/googlePlaces";
import { usePlacesPredictions } from "@src/packages/hooks/usePlacesPredictions";
import {
  type DiagnosticProfile,
  type TreatmentType,
} from "@src/packages/homepage/utils/costEstimator";
import {
  formatCurrency,
  formatDate,
  type Locale,
  RegionGate,
  type TranslationFunction,
  useLocale,
  useRegion,
  useTranslations,
} from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { usePatient } from "@src/packages/patients/hooks/usePatient";
import { resolvePlannedModality } from "@src/packages/homepage/plan/resolvePlannedModality";
import type { AcceptedTreatmentPlan } from "@src/packages/homepage/plan/treatmentPlanTypes";
import { normalizePlannedModalities } from "../../access/findCareIntent";
import { useAuthorizedExperienceProjection } from "@src/packages/experience/hooks/useAuthorizedExperienceProjection";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionChevron,
  AccordionTrigger,
} from "@louise/moon-ui/primitives/Accordion";
import type { GradientVariant } from "@louise/moon-ui/primitives/GradientIcon";
import {
  HEADER_INSET,
  SCREEN_PADDING_TOP,
  colors,
} from "@louise/moon-ui/theme/tokens";
import { EMBER } from "../../../../onboarding/theme";
import {
  apiToUiPaymentRecord,
  parseDateText,
  todayIsoDate,
  uiToPaymentRecordUpdates,
} from "../paymentRecordMapping";
import { summarizePaymentRecords } from "../paymentRecordSummary";
import {
  hasCostDocumentUpload,
  mergeCostDocumentReferences,
  toAttachedCostDocumentReference,
  type FiledCostDocument,
} from "@src/packages/costs/document-linkage";
import { toDecimalAmount } from "@src/packages/costs/decimalAmount";
import { detachDocumentFromPaymentRecord } from "../taxReviewHelpers";
import {
  currentFinancingProviders,
  hasCurrentPromotionalFinancing,
} from "@src/packages/costs/financialContent";
import { useOnboarding } from "../../../../onboarding/OnboardingContext";
import type { TreatmentIntent } from "../../../../onboarding/types";
import type {
  CostPaymentLineItemParam,
  CostPaymentRecordKind,
  CostPaymentRecordParam,
  CostPaymentsRouteParams,
  CostsStackParamList,
  MainTabParamList,
} from "../../../types";
import InfoSheet, { InfoButton } from "../../../components/InfoSheet";
import PillSelect from "../../../components/PillSelect";
import { useEstimateLocationIntent } from "../../../hooks/useEstimateLocationIntent";
import { useFlowAnalytics } from "@src/packages/analytics/useFlowAnalytics";
import ScreenHeader from "../../../components/ScreenHeader";
import StatusBadge from "../../../components/StatusBadge";
import ViewAllLink from "../../../components/ViewAllLink";
import CostsCardTabs from "../../../components/CostsCardTabs";
import InsuranceInfoModal from "../../YouScreen/modals/InsuranceInfoModal";
import ChatWithUsCard from "../../CostsScreen/ChatWithUsCard";
import CompareClinicCostsCard from "../../CostsScreen/CompareClinicCostsCard";
import FaqSection from "../../CostsScreen/FaqSection";
import InsuranceGlossaryCard from "../../CostsScreen/InsuranceGlossaryCard";
import { GRANTS } from "../grants.data";
import PaymentRecordDetailModal, {
  buildPaymentRecordChatPrompt,
} from "../PaymentRecordDetailModal";
import { semanticColors, withAlpha } from "@louise/theme/tokens";

const COST_ESTIMATE_ILLUSTRATION = require("../../../../assets/images/cost-estimate-coins.png");
const COST_QUOTE_RECEIPT_ILLUSTRATION = require("../../../../assets/images/cost-quote-receipt.png");
const DELETE_CONFIRMATION_ILLUSTRATION =
  require("../../../../assets/images/you-icons/trash-bin.png") as number;
const ORGANIC_BAR_COLORS = {
  paid: "#F4B34F",
  claims: "#5B332C",
  left: "#B76545",
} as const;
const PAYMENT_ROW_ICON_COLOR = colors.greyBrown;
const PAYMENT_RECORD_PREVIEW_LIMIT = 4;

type Props = NativeStackScreenProps<CostsStackParamList, "CostsOverview">;

type ConceptCostState = "estimate" | "clinic-estimate" | "payments";
type StatusBadgeTone =
  "neutral" | "gold" | "coral" | "green" | "orange" | "blue" | "purple";
type CostsOverviewRouteName = Extract<
  Exclude<keyof CostsStackParamList, "CostsOverview" | "GrantDetail">,
  string
>;
type CostsOverviewRouteParams = { paymentRecordId?: string };

type CategoryCard = {
  title: string;
  subtitle: string;
  icon: LucideIcon;
  iconVariant: GradientVariant;
  route: CostsOverviewRouteName;
  /** i18n key of `title`, kept stable for analytics across locales. */
  action: string;
};

type CostStateSummary = {
  eyebrow: string;
  title: string;
  amount: string;
  amountLabel: string;
  description: string;
  badge: string;
  badgeTone: "estimate" | "quote" | "payments";
  rows: { label: string; value: string; detail?: string }[];
  primaryAction: string;
  /** i18n key of `primaryAction`, kept stable for analytics across locales. */
  primaryActionKey: string;
  secondaryAction: string;
  /** i18n key of `secondaryAction`, kept stable for analytics across locales. */
  secondaryActionKey: string;
  primaryRoute?: CostsOverviewRouteName;
  secondaryRoute?: CostsOverviewRouteName;
};

export type CostUploadMode = "quote" | "payment";
export type ManualPaymentKind = "payment" | "claim" | "receipt" | "balance";

/** Charge categories shown as chips at the Add-payment step so a manual
 *  entry can be routed into the same line-item structure that OCR produces
 *  for uploaded receipts. "Other" leaves it unset (free-text label takes
 *  over). Kept intentionally short — picked from LINE_ITEM_PRESETS. */
export type ManualPaymentCategory =
  "medication" | "consult" | "lab" | "procedure" | "storage" | "other";

const MANUAL_PAYMENT_CATEGORY_PRESETS: {
  id: Exclude<ManualPaymentCategory, "other">;
  label: string;
}[] = [
  { id: "medication", label: "Medication" },
  { id: "consult", label: "Consult" },
  { id: "lab", label: "Lab" },
  { id: "procedure", label: "Procedure" },
  { id: "storage", label: "Storage" },
];

/** Record kinds offered by the payment upload form, in display order. */
const PAYMENT_RECORD_KINDS = [
  ["payment", "costs.cost_upload.record_kinds.payment"],
  ["claim", "costs.cost_upload.record_kinds.claim"],
  ["receipt", "costs.cost_upload.record_kinds.receipt"],
  ["balance", "costs.cost_upload.record_kinds.balance"],
] as const satisfies readonly (readonly [ManualPaymentKind, string])[];

type QuoteOCRResult = {
  clinicName: string | null;
  quoteDate: string | null;
  currency: string | null;
  totalAmount: string | null;
  lineItems: {
    label: string | null;
    amount: string | null;
    category: string | null;
    notes: string | null;
  }[];
};

export type UploadedCostDocument = {
  id: string;
  mode: CostUploadMode;
  source: "camera" | "document";
  name: string;
  uri: string;
  mimeType: string;
  paymentKind?: ManualPaymentKind;
  /** S3 uploadId once the file is filed — used to link the canonical source. */
  uploadId?: string | null;
  /** Canonical Documents record ID once the file is filed. */
  documentId?: string | null;
};

export type ManualPaymentEntry = {
  id: string;
  kind: ManualPaymentKind;
  label: string;
  /** Human label for the auto-created line item — e.g. "Medication".
   *  `null` means the user left it blank or picked "Other"; the record's
   *  free-text label is used instead when a line item is synthesized. */
  category: string | null;
  provider: string;
  amount: number;
  date: string;
  note: string;
  attachmentName?: string;
  attachmentUploadId?: string | null;
  attachmentDocumentId?: string | null;
};

type PendingCostUpload = Pick<
  UploadedCostDocument,
  "source" | "name" | "uri" | "mimeType"
> & {
  docName: string;
  tags: string[];
  uploadId?: string;
};

type QuoteAnalysisStatus = "ready" | "available" | "needs-upload";

type QuoteLineItem = {
  key: string;
  label: string;
  amount: number;
  category: string | null;
  status: string;
  coverage: string;
  financing: string;
  detail: string;
};

type ClinicQuote = {
  id: string;
  clinic: string;
  location: string;
  logoText: string;
  status: string;
  statusTone: "ready" | "pending" | "attention";
  quoteTotal: number;
  outOfPocket: number;
  uploadedAt: string;
  documentLabel: string;
  quoteDate: string | null;
  summary: string;
  analysisStatus: QuoteAnalysisStatus;
  analysisTitle: string;
  analysisBody: string;
  missingItems: string[];
  lineItems: QuoteLineItem[];
};

export type QuoteComparisonContext = {
  quotes: ClinicQuote[];
  treatmentLabel: string;
  careLocation: string;
  hasInsurance: boolean;
  verificationStatus: "unverified" | "pending" | "verified";
  primaryPolicy: InsurancePolicy | null;
  fertilityCoverage: FertilityCoverage | null;
};

function formatChatContext(value: unknown, notAvailable: string) {
  if (value == null) return notAvailable;
  return JSON.stringify(value, null, 2) ?? notAvailable;
}

export function buildQuoteComparisonChatPrompt(
  context: QuoteComparisonContext,
  locale: Locale,
  t: TranslationFunction,
) {
  const prompt = (key: string, params?: Record<string, string>) =>
    t(`costs.quote_comparison_prompt.${key}`, params ? { params } : undefined);
  const notAvailable = prompt("not_available");
  const quoteSections = context.quotes.map((quote, index) => {
    const lineItems = quote.lineItems.length
      ? quote.lineItems
          .map(
            (line) =>
              `  - ${line.label}: ${formatCurrency(line.amount, locale)} · ${line.status} · ${line.coverage} · ${line.financing} · ${line.detail}`,
          )
          .join("\n")
      : `  - ${prompt("no_readable_line_items")}`;

    return [
      prompt("quote_number", { number: String(index + 1) }),
      `- ${prompt("clinic", { clinic: quote.clinic })}`,
      `- ${prompt("location", { location: quote.location })}`,
      `- ${prompt("document", { document: quote.documentLabel })}`,
      `- ${prompt("status", { status: quote.status })}`,
      `- ${prompt("quote_total", {
        amount: formatCurrency(quote.quoteTotal, locale),
      })}`,
      `- ${prompt("estimated_out_of_pocket", {
        amount: formatCurrency(quote.outOfPocket, locale),
      })}`,
      `- ${prompt("quote_summary", { summary: quote.summary })}`,
      `- ${prompt("analysis", {
        analysis: quote.analysisTitle,
        detail: quote.analysisBody,
      })}`,
      `- ${prompt("missing_items", {
        items: quote.missingItems.join(", ") || prompt("none_listed"),
      })}`,
      `- ${prompt("line_items")}`,
      lineItems,
    ].join("\n");
  });

  return [
    prompt("compare_uploaded_quotes"),
    prompt("use_every_detail"),
    prompt("comparison_context"),
    `- ${prompt("treatment_path", { treatment: context.treatmentLabel })}`,
    `- ${prompt("care_location", { location: context.careLocation })}`,
    prompt("insurance_details"),
    `- ${prompt(
      context.hasInsurance ? "insurance_added_yes" : "insurance_added_no",
    )}`,
    `- ${prompt("verification_status", {
      status: context.verificationStatus,
    })}`,
    `- ${prompt("policy_details")}`,
    formatChatContext(context.primaryPolicy, notAvailable),
    `- ${prompt("fertility_coverage_details")}`,
    formatChatContext(context.fertilityCoverage, notAvailable),
    prompt("uploaded_quotes"),
    quoteSections.join("\n\n") || prompt("no_quotes_available"),
    prompt("side_by_side_request"),
  ].join("\n");
}

type PaymentRecordStatus =
  | "paid"
  | "submitted"
  | "waiting-eob"
  | "needs-receipt"
  | "partially-covered"
  | "ready-reimbursement"
  | "denied"
  | "due"
  | "review";

type PaymentSplitLine = {
  id: string;
  label: string;
  amount: number;
  detail: string;
  color: string;
};

type PaymentDocumentLine = {
  id: string;
  label: string;
  detail: string;
  status: "attached" | "needed" | "pending";
  uploadId?: string | null;
};

type PaymentRecord = {
  id: string;
  kind?: CostPaymentRecordKind;
  label: string;
  source: string;
  provider: string;
  phase: string;
  amount: number;
  billedAmount: number;
  status: PaymentRecordStatus;
  date: string;
  headline: string;
  detail: string;
  coverage: string;
  financing: string;
  nextAction: string;
  splitLines: PaymentSplitLine[];
  lineItems?: CostPaymentLineItemParam[];
  documents: PaymentDocumentLine[];
};

type PaymentBreakdownSegment = {
  label: string;
  value: number;
  detail: string;
  color: string;
};

const TREATMENT_LABEL_KEYS: Record<TreatmentType, string> = {
  egg_freezing: "costs.money_card.treatments.egg_freezing",
  embryo_freezing: "costs.money_card.treatments.embryo_freezing",
  sperm_freezing: "costs.money_card.treatments.sperm_freezing",
  ivf: "costs.money_card.treatments.ivf",
  ivf_icsi: "costs.money_card.treatments.ivf_icsi",
  fet: "costs.money_card.treatments.fet",
  iui: "costs.money_card.treatments.iui",
  ovulation_induction: "costs.money_card.treatments.ovulation_induction",
};

// The treatment as it reads mid-sentence in the scenario summary ("For egg
// freezing using U.S. averages, ..."). French needs an article here, so this
// is its own copy rather than a lowercased label.
const TREATMENT_IN_SENTENCE_KEYS: Record<TreatmentType, string> = {
  egg_freezing:
    "costs.money_card.scenario_summary.treatment_in_sentence.egg_freezing",
  embryo_freezing:
    "costs.money_card.scenario_summary.treatment_in_sentence.embryo_freezing",
  sperm_freezing:
    "costs.money_card.scenario_summary.treatment_in_sentence.sperm_freezing",
  ivf: "costs.money_card.scenario_summary.treatment_in_sentence.ivf",
  ivf_icsi: "costs.money_card.scenario_summary.treatment_in_sentence.ivf_icsi",
  fet: "costs.money_card.scenario_summary.treatment_in_sentence.fet",
  iui: "costs.money_card.scenario_summary.treatment_in_sentence.iui",
  ovulation_induction:
    "costs.money_card.scenario_summary.treatment_in_sentence.ovulation_induction",
};

const CONCEPT_TREATMENT_OPTIONS: TreatmentType[] = [
  "egg_freezing",
  "ivf",
  "ivf_icsi",
  "iui",
  "fet",
  "embryo_freezing",
  "sperm_freezing",
  "ovulation_induction",
];

const ESTIMATE_INFO_SECTION_KEYS = [
  {
    titleKey: "costs.money_card.how_it_works.what_changes_the_estimate",
    icon: SlidersHorizontal,
    bodyKey: "costs.money_card.how_it_works.estimate_drivers_explained",
  },
  {
    titleKey: "costs.money_card.how_it_works.what_is_inside",
    icon: Package,
    bodyKey: "costs.money_card.how_it_works.estimate_contents_explained",
  },
  {
    titleKey: "costs.money_card.how_it_works.clinic_quotes",
    icon: FileText,
    bodyKey: "costs.money_card.how_it_works.clinic_quotes_explained",
  },
  {
    titleKey: "costs.money_card.how_it_works.payments_and_claims",
    icon: Receipt,
    bodyKey: "costs.money_card.how_it_works.payments_and_claims_explained",
  },
  {
    titleKey: "costs.money_card.how_it_works.location_and_logistics",
    icon: MapPin,
    bodyKey: "costs.money_card.how_it_works.location_tradeoffs_explained",
  },
];

type ConceptLocationOption = EstimateAreaOption & {
  action?: "request-current-location";
};

function getAcceptedPlanTreatmentType(
  treatmentFamily: AcceptedTreatmentPlan["treatmentFamily"] | null | undefined,
): TreatmentType | null {
  switch (treatmentFamily) {
    case "ivf":
      return "ivf";
    case "iui":
      return "iui";
    case "egg_freezing":
      return "egg_freezing";
    case "embryo_freezing":
      return "embryo_freezing";
    case "sperm_freezing":
      return "sperm_freezing";
    case "fet":
      return "fet";
    case "oi":
      return "ovulation_induction";
    default:
      return null;
  }
}

function getCanonicalTreatmentType(
  treatmentGoal: string | null | undefined,
  plannedModality: "ivf" | "iui" | "fet" | null,
): TreatmentType | null {
  if (plannedModality) return plannedModality;

  switch (treatmentGoal) {
    case "ivf":
      return "ivf";
    case "iui":
      return "iui";
    case "fet":
      return "fet";
    case "egg-freezing":
      return "egg_freezing";
    case "embryo-freezing":
      return "embryo_freezing";
    case "sperm-freezing":
      return "sperm_freezing";
    default:
      // Broad pregnancy, preservation, and exploration intents do not
      // authorize a treatment-specific estimate without an explicit path.
      return null;
  }
}
function getUrlCostState(): ConceptCostState | null {
  if (!__DEV__ || typeof window === "undefined") return null;
  try {
    const params = new URLSearchParams(window.location.search);
    return normalizeConceptCostState(
      params.get("dev_costs_state") ??
        params.get("dev_costs_suggested") ??
        params.get("dev_costs_surface"),
    );
  } catch {
    return null;
  }
}

export function getDevSuggestedCostsSurface(): string | null {
  if (!__DEV__ || typeof window === "undefined") return null;
  try {
    const params = new URLSearchParams(window.location.search);
    return (
      params.get("dev_costs_state") ??
      params.get("dev_costs_suggested") ??
      params.get("dev_costs_surface")
    );
  } catch {
    return null;
  }
}

// Dev-only fixture that replays a resolved location on mount so the
// arbitrary-location states render deterministically for visual evidence,
// without a live Places call (CI has no Maps key). Formats:
//   ?dev_costs_location=Tucson, AZ        US city -> nearest-metro fallback
//   ?dev_costs_location=Lyon|FR|France    non-US city with country-only cover
//   ?dev_costs_location=Toronto|CA|Canada country with no coverage
function getDevCostsLocationFixture(): PlaceEstimateInput | null {
  if (!__DEV__ || typeof window === "undefined") return null;
  let raw: string | null = null;
  try {
    raw = new URLSearchParams(window.location.search).get("dev_costs_location");
  } catch {
    return null;
  }
  const value = raw?.trim();
  if (!value) return null;

  if (value.includes("|")) {
    const [cityPart, countryCode, countryName] = value
      .split("|")
      .map((part) => part.trim());
    return {
      granularity: "city",
      label: cityPart || countryName || countryCode || value,
      city: cityPart || null,
      countryCode: countryCode || "US",
      countryName: countryName || null,
    };
  }

  const parts = value
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  const city = parts[0] ?? value;
  const stateCode = parts[1] ?? null;
  return {
    granularity: "city",
    label: stateCode ? `${city}, ${stateCode}` : city,
    city,
    stateCode,
    countryCode: "US",
    countryName: "United States",
  };
}

export function getDevPaymentRecordFixtures(): CostPaymentRecordParam[] | null {
  if (!__DEV__ || typeof window === "undefined") return null;

  let state: string | null = null;
  try {
    const params = new URLSearchParams(window.location.search);
    state = params.get("dev_costs_payment_record");
  } catch {
    return null;
  }

  const deniedClaim: CostPaymentRecordParam = {
    id: "dev-denied-claim",
    kind: "claim",
    label: "IVF cycle claim",
    source: "Insurance claim",
    provider: "Northstar Health",
    phase: "Post-cycle",
    amount: 2400,
    billedAmount: 3000,
    status: "denied",
    date: "Jun 18, 2026",
    headline: "$2,400 denied",
    detail: "Claim denied after coverage review.",
    coverage: "Denied by plan",
    financing: "Not financed yet",
    nextAction: "Review insurance",
    splitLines: [
      {
        id: "dev-denied-claim-insurance",
        label: "Insurance denied",
        amount: 2400,
        detail: "Coverage review required",
        color: ORGANIC_BAR_COLORS.claims,
      },
      {
        id: "dev-denied-claim-patient",
        label: "Patient responsibility",
        amount: 600,
        detail: "Expected balance",
        color: ORGANIC_BAR_COLORS.left,
      },
    ],
    lineItems: [
      {
        id: "dev-denied-claim-line-item",
        label: "IVF cycle",
        amount: 3000,
        detail: "Cycle treatment claim",
        allocations: [
          {
            id: "dev-denied-claim-allocation-insurance",
            kind: "insurance",
            label: "Insurance",
            amount: 2400,
          },
          {
            id: "dev-denied-claim-allocation-patient",
            kind: "patient",
            label: "You",
            amount: 600,
          },
        ],
      },
    ],
    documents: [],
  };

  if (state === "denied") return [deniedClaim];
  if (state !== "filled") return null;

  return [
    {
      ...deniedClaim,
      id: "dev-filled-payment",
      kind: "payment",
      label: "Clinic deposit",
      source: "Clinic payment",
      provider: "Northstar Health",
      phase: "Before cycle",
      amount: 1200,
      billedAmount: 1200,
      status: "paid",
      date: "Jun 12, 2026",
      headline: "$1,200 recorded",
      detail: "Deposit recorded against the clinic plan.",
      coverage: "Save for reimbursement review",
      nextAction: "Add document",
      splitLines: [
        {
          id: "dev-filled-payment-you",
          label: "Paid by you",
          amount: 1200,
          detail: "Clinic deposit",
          color: ORGANIC_BAR_COLORS.paid,
        },
      ],
      lineItems: [],
    },
    deniedClaim,
    {
      ...deniedClaim,
      id: "dev-filled-receipt",
      kind: "receipt",
      label: "Medication receipt",
      source: "Pharmacy receipt",
      provider: "Harbor Pharmacy",
      phase: "Medication",
      amount: 380,
      billedAmount: 380,
      status: "needs-receipt",
      date: "Jun 8, 2026",
      headline: "$380 receipt",
      detail: "Medication receipt awaiting document match.",
      coverage: "Confirm coverage path",
      nextAction: "Add document",
      splitLines: [
        {
          id: "dev-filled-receipt-you",
          label: "Paid by you",
          amount: 380,
          detail: "Medication",
          color: ORGANIC_BAR_COLORS.paid,
        },
      ],
      lineItems: [],
      documents: [
        {
          id: "dev-filled-receipt-needed",
          label: "Supporting document",
          detail: "Add the pharmacy receipt when available",
          status: "needed",
        },
      ],
    },
    {
      ...deniedClaim,
      id: "dev-filled-balance",
      kind: "balance",
      label: "Remaining clinic balance",
      source: "Clinic statement",
      provider: "Northstar Health",
      phase: "Before cycle",
      amount: 650,
      billedAmount: 650,
      status: "due",
      date: "Jun 20, 2026",
      headline: "$650 left to pay",
      detail: "Balance from the latest clinic statement.",
      coverage: "Confirm coverage path",
      financing: "Can plan now",
      nextAction: "Review document",
      splitLines: [
        {
          id: "dev-filled-balance-patient",
          label: "Patient responsibility",
          amount: 650,
          detail: "Remaining balance",
          color: ORGANIC_BAR_COLORS.left,
        },
      ],
      lineItems: [],
    },
  ];
}

function getDevClinicQuoteFixtures(): ApiClinicQuote[] {
  if (!__DEV__ || typeof window === "undefined") return [];

  let state: string | null = null;
  try {
    const params = new URLSearchParams(window.location.search);
    state = params.get("dev_costs_quote_state");
  } catch {
    return [];
  }

  if (state !== "error" && state !== "filled") return [];

  const now = new Date().toISOString();
  if (state === "error") {
    return [
      {
        id: "dev-unreadable-clinic-quote",
        patientId: "dev-patient",
        name: "Unreadable clinic quote.pdf",
        uploadId: null,
        mimeType: "application/pdf",
        extraction: null,
        source: "patient",
        verificationStatus: "unverified",
        verifiedAt: null,
        verifiedBy: null,
        verificationNotes: null,
        createdAt: now,
        updatedAt: now,
        updatedBy: null,
      },
    ];
  }

  return [
    {
      id: "dev-filled-clinic-quote-a",
      patientId: "dev-patient",
      name: "Northstar Fertility quote.pdf",
      uploadId: null,
      mimeType: "application/pdf",
      extraction: {
        clinicName: "Northstar Fertility",
        quoteDate: "2026-06-18",
        currency: "USD",
        totalAmount: "14800",
        lineItems: [
          {
            label: "IVF cycle package",
            amount: "9800",
            category: "Treatment",
            notes: "Includes monitoring and retrieval.",
          },
          {
            label: "Embryology and lab fees",
            amount: "3200",
            category: "Laboratory",
            notes: "Includes fertilization and embryo culture.",
          },
          {
            label: "Anesthesia",
            amount: "1800",
            category: "Procedure",
            notes: "Billed by a separate anesthesia group.",
          },
        ],
      },
      source: "patient",
      verificationStatus: "unverified",
      verifiedAt: null,
      verifiedBy: null,
      verificationNotes: null,
      createdAt: now,
      updatedAt: now,
      updatedBy: null,
    },
    {
      id: "dev-filled-clinic-quote-b",
      patientId: "dev-patient",
      name: "Harbor Reproductive Care quote.pdf",
      uploadId: null,
      mimeType: "application/pdf",
      extraction: {
        clinicName: "Harbor Reproductive Care",
        quoteDate: "2026-06-21",
        currency: "USD",
        totalAmount: "16250",
        lineItems: [
          {
            label: "IVF cycle package",
            amount: "11250",
            category: "Treatment",
            notes: "Includes monitoring, retrieval, and transfer planning.",
          },
          {
            label: "Embryology and lab fees",
            amount: "3200",
            category: "Laboratory",
            notes: "ICSI is optional and not included.",
          },
          {
            label: "Anesthesia and facility fee",
            amount: "1800",
            category: "Procedure",
            notes: "Facility fee is included; anesthesia provider to confirm.",
          },
        ],
      },
      source: "patient",
      verificationStatus: "unverified",
      verifiedAt: null,
      verifiedBy: null,
      verificationNotes: null,
      createdAt: now,
      updatedAt: now,
      updatedBy: null,
    },
  ];
}

export function normalizeConceptCostState(
  raw: string | null | undefined,
): ConceptCostState | null {
  if (raw === "estimate" || raw === "exploring") return "estimate";
  if (
    raw === "quote" ||
    raw === "quotes" ||
    raw === "clinic" ||
    raw === "clinic-estimate"
  ) {
    return "clinic-estimate";
  }
  if (raw === "payments" || raw === "claims" || raw === "active") {
    return "payments";
  }
  return null;
}

function deriveConceptCostState(params: {
  intent: TreatmentIntent | null;
  comparisonCount: number;
  verifiedClinicCount: number;
}): ConceptCostState {
  const normalizedIntent = params.intent?.toLowerCase();
  if (
    normalizedIntent === "intreatment" ||
    normalizedIntent === "startingtreatment"
  ) {
    return "payments";
  }

  if (params.verifiedClinicCount > 0 || params.comparisonCount > 0) {
    return "clinic-estimate";
  }

  return "estimate";
}

function estimatePatientResponsibility(
  totalCost: number,
  fertilityCoverage: FertilityCoverage | null,
): number | null {
  if (!fertilityCoverage) return null;

  const deductible = fertilityCoverage.deductibleCents
    ? fertilityCoverage.deductibleCents / 100
    : 0;
  const coinsurance =
    fertilityCoverage.coinsurancePercent != null
      ? fertilityCoverage.coinsurancePercent / 100
      : 0.2;
  const oopMax =
    fertilityCoverage.outOfPocketMaxCents != null
      ? fertilityCoverage.outOfPocketMaxCents / 100
      : Number.POSITIVE_INFINITY;

  const afterDeductible = Math.max(totalCost - deductible, 0);
  const patientShare =
    totalCost <= deductible
      ? totalCost
      : deductible + afterDeductible * coinsurance;

  return Math.min(patientShare, oopMax);
}

export function CostDocumentUploadModal({
  visible,
  mode,
  documentOnly = false,
  onClose,
  onDocumentPicked,
  onManualPaymentAdded,
  initialPaymentKind,
}: {
  visible: boolean;
  mode: CostUploadMode;
  documentOnly?: boolean;
  onClose: () => void;
  onDocumentPicked: (document: UploadedCostDocument) => void | Promise<void>;
  onManualPaymentAdded: (entry: ManualPaymentEntry) => void | Promise<void>;
  initialPaymentKind?: ManualPaymentKind;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("CostDocumentUploadModal");
  const [busySource, setBusySource] = useState<
    UploadedCostDocument["source"] | null
  >(null);
  const [paymentKind, setPaymentKind] = useState<ManualPaymentKind>(
    initialPaymentKind ?? "payment",
  );
  const [paymentCategory, setPaymentCategory] =
    useState<ManualPaymentCategory>("other");
  const [paymentLabel, setPaymentLabel] = useState("");
  const [paymentProvider, setPaymentProvider] = useState("");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentDate, setPaymentDate] = useState("");
  const [paymentNote, setPaymentNote] = useState("");
  const [paymentAttachmentName, setPaymentAttachmentName] = useState<
    string | null
  >(null);
  const [paymentAttachmentUri, setPaymentAttachmentUri] = useState<
    string | null
  >(null);
  const [paymentAttachmentMimeType, setPaymentAttachmentMimeType] = useState(
    "application/octet-stream",
  );
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [pendingDocument, setPendingDocument] =
    useState<UploadedCostDocument | null>(null);
  const [pendingUpload, setPendingUpload] = useState<PendingCostUpload | null>(
    null,
  );
  const [pendingPaymentAttachment, setPendingPaymentAttachment] =
    useState<UploadedCostDocument | null>(null);

  const transfer = useDocumentSourceTransfer();

  useEffect(() => {
    if (visible && mode === "payment" && initialPaymentKind) {
      setPaymentKind(initialPaymentKind);
    }
  }, [initialPaymentKind, mode, visible]);

  // Tag derivation: route cost uploads into the central Documents folder so
  // they show up alongside other records. Manual-payment attachments use the
  // payment kind to land in the closest matching slot/category.
  const tagsForQuote = (): string[] => ["coverage_benefits"];
  const tagsForPaymentMode = (): string[] => ["clinic-invoices", "admin"];
  const tagsForPaymentKind = (kind: ManualPaymentKind): string[] => {
    switch (kind) {
      case "claim":
        return ["eob", "coverage_benefits"];
      case "receipt":
        return ["pharmacy-receipts", "admin"];
      case "payment":
      case "balance":
      default:
        return ["clinic-invoices", "admin"];
    }
  };

  // Upload a durable source and wait for its canonical processing run. The
  // pending upload keeps the completed upload ID when processing fails so a
  // retry can resume without uploading the file again.
  const uploadAndFileDocument = async (
    file: {
      uri: string;
      name: string;
      mimeType: string;
      uploadId?: string | null;
    },
    docName: string,
    tags: string[],
  ): Promise<FiledCostDocument | null> => {
    setUploadError(null);
    try {
      const source = file.uploadId
        ? { file, uploadId: file.uploadId }
        : await transfer.uploadSource(file, {
            destination: "document",
            sourceContext: {
              surface: "cost_document_upload_modal",
              form: mode,
              targetHints: tags,
              allowBackgroundProcessing: true,
            },
          });
      setPendingUpload((pending) =>
        pending ? { ...pending, uploadId: source.uploadId } : pending,
      );
      // 60 × 2s = 2 minutes. Longer than the previous 30s so dev-backend
      // processing has room to finish, but still bounded so a stuck job
      // doesn't lock the modal forever.
      const outcome = await transfer.processSource(source, {
        attempts: 60,
        intervalMs: 2_000,
      });
      return {
        documentId: outcome.patientDocumentId,
        uploadId: source.uploadId,
        name: file.name,
      };
    } catch (uploadError) {
      console.error("Failed to upload cost document:", uploadError);
      // Distinguish "still processing" (file is safe on the server, Louise
      // just hasn't finished reading it) from a real upload failure so the
      // user doesn't think they lost the file.
      const message =
        uploadError instanceof Error &&
        uploadError.message === "The document is still processing"
          ? t("costs.cost_upload.file_saved_louise_still_reading")
          : t("costs.cost_upload.couldnt_save_file");
      setUploadError(message);
      return null;
    }
  };

  if (!visible) return null;

  const resetPaymentDraft = () => {
    setPaymentKind("payment");
    setPaymentCategory("other");
    setPaymentLabel("");
    setPaymentProvider("");
    setPaymentAmount("");
    setPaymentDate("");
    setPaymentNote("");
    setPaymentAttachmentName(null);
    setPaymentAttachmentUri(null);
    setPaymentAttachmentMimeType("application/octet-stream");
    setPendingDocument(null);
    setPendingUpload(null);
    setPendingPaymentAttachment(null);
  };

  const handleClose = () => {
    resetPaymentDraft();
    setUploadError(null);
    onClose();
  };

  const complete = async (
    source: UploadedCostDocument["source"],
    name: string,
    uri: string,
    mimeType: string,
  ) => {
    const docName =
      mode === "quote"
        ? `Clinic quote – ${name}`
        : `Payment document – ${name}`;
    const tags = mode === "quote" ? tagsForQuote() : tagsForPaymentMode();
    const upload: PendingCostUpload = {
      source,
      name,
      uri,
      mimeType,
      docName,
      tags,
    };
    setPendingUpload(upload);
    const filedDocument = await uploadAndFileDocument(
      { uri, name, mimeType },
      docName,
      tags,
    );
    if (!filedDocument) return;
    setPendingUpload(null);

    const uploadedDocument: UploadedCostDocument = {
      id: `${mode}-${Date.now()}`,
      mode,
      source,
      name,
      uri,
      mimeType,
      paymentKind: mode === "payment" ? paymentKind : undefined,
      uploadId: filedDocument.uploadId,
      documentId: filedDocument.documentId,
    };
    setPendingDocument(uploadedDocument);

    try {
      await onDocumentPicked(uploadedDocument);
      setPendingDocument(null);
      handleClose();
    } catch (error) {
      console.error("Failed to save uploaded cost record:", error);
      setUploadError(t("costs.cost_upload.couldnt_attach_file"));
    }
  };

  const retryPendingDocument = async () => {
    if (!pendingDocument) return;
    setUploadError(null);
    try {
      await onDocumentPicked(pendingDocument);
      setPendingDocument(null);
      handleClose();
    } catch (error) {
      console.error("Failed to save uploaded cost record:", error);
      setUploadError(t("costs.cost_upload.couldnt_attach_file"));
    }
  };

  const retryPendingUpload = async () => {
    if (!pendingUpload) return;
    setUploadError(null);
    try {
      const filedDocument = await uploadAndFileDocument(
        pendingUpload,
        pendingUpload.docName,
        pendingUpload.tags,
      );
      if (!filedDocument) return;
      setPendingUpload(null);
      const uploadedDocument: UploadedCostDocument = {
        id: `${mode}-${Date.now()}`,
        mode,
        source: pendingUpload.source,
        name: pendingUpload.name,
        uri: pendingUpload.uri,
        mimeType: pendingUpload.mimeType,
        paymentKind: mode === "payment" ? paymentKind : undefined,
        uploadId: filedDocument.uploadId,
        documentId: filedDocument.documentId,
      };
      setPendingDocument(uploadedDocument);
      await onDocumentPicked(uploadedDocument);
      setPendingDocument(null);
      handleClose();
    } catch (error) {
      console.error("Failed to retry cost document upload:", error);
      setUploadError(t("costs.cost_upload.couldnt_save_file"));
    }
  };

  const pickPaymentAttachment = async () => {
    setBusySource("document");
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: ["application/pdf", "image/*"],
        copyToCacheDirectory: true,
      });

      if (!result.canceled && result.assets?.[0]) {
        const file = result.assets[0];
        setUploadError(null);
        setPaymentAttachmentName(file.name);
        setPaymentAttachmentUri(file.uri);
        setPaymentAttachmentMimeType(
          resolveUploadContentType({
            name: file.name,
            uri: file.uri,
            mimeType: file.mimeType,
          }),
        );
        setPendingPaymentAttachment(null);
      }
    } finally {
      setBusySource(null);
    }
  };

  const parsedPaymentAmount = Number(paymentAmount.replace(/[^0-9.]/g, ""));
  // A ledger entry needs at least a real amount or an attached document —
  // otherwise "Add record" would file an empty $0 placeholder with no source.
  const canSaveManualPayment =
    (Number.isFinite(parsedPaymentAmount) && parsedPaymentAmount > 0) ||
    paymentAttachmentName != null;

  const saveManualPayment = async () => {
    if (!canSaveManualPayment) return;
    const numericAmount = parsedPaymentAmount;
    const defaultLabel: Record<ManualPaymentKind, string> = {
      payment: t("costs.cost_upload.unnamed_records.payment_record"),
      claim: t("costs.cost_upload.unnamed_records.insurance_claim"),
      receipt: t("costs.cost_upload.unnamed_records.receipt"),
      balance: t("costs.cost_upload.unnamed_records.remaining_balance"),
    };

    try {
      let filedAttachment: FiledCostDocument | null = pendingPaymentAttachment
        ? pendingPaymentAttachment.documentId &&
          pendingPaymentAttachment.uploadId
          ? {
              documentId: pendingPaymentAttachment.documentId ?? "",
              uploadId: pendingPaymentAttachment.uploadId ?? "",
              name: pendingPaymentAttachment.name,
            }
          : null
        : null;
      if (paymentAttachmentUri && paymentAttachmentName) {
        if (!filedAttachment?.documentId || !filedAttachment.uploadId) {
          const pendingAttachment: UploadedCostDocument = {
            id: pendingPaymentAttachment?.id ?? `payment-${Date.now()}`,
            mode: "payment",
            source: "document",
            name: paymentAttachmentName,
            uri: paymentAttachmentUri,
            mimeType: paymentAttachmentMimeType,
            paymentKind,
          };
          setPendingPaymentAttachment(pendingAttachment);
          setBusySource("document");
          filedAttachment = await uploadAndFileDocument(
            pendingAttachment,
            `Payment document – ${paymentAttachmentName}`,
            tagsForPaymentKind(paymentKind),
          );
          setBusySource(null);
          if (!filedAttachment) return;
          setPendingPaymentAttachment({
            ...pendingAttachment,
            uploadId: filedAttachment.uploadId,
            documentId: filedAttachment.documentId,
          });
        }
      }
      const categoryLabel =
        paymentCategory === "other"
          ? null
          : (MANUAL_PAYMENT_CATEGORY_PRESETS.find(
              (preset) => preset.id === paymentCategory,
            )?.label ?? null);
      await onManualPaymentAdded({
        id: `manual-payment-${Date.now()}`,
        kind: paymentKind,
        category: categoryLabel,
        label: paymentLabel.trim() || defaultLabel[paymentKind],
        provider:
          paymentProvider.trim() || t("costs.cost_upload.provider_not_set"),
        amount: Number.isFinite(numericAmount) ? numericAmount : 0,
        date: paymentDate.trim() || "Added today",
        note: paymentNote.trim(),
        attachmentName: paymentAttachmentName ?? undefined,
        attachmentUploadId: filedAttachment?.uploadId ?? null,
        attachmentDocumentId: filedAttachment?.documentId ?? null,
      });
      setPendingPaymentAttachment(null);
      handleClose();
    } catch (error) {
      console.error("Failed to save manual payment record:", error);
      setUploadError(t("costs.cost_upload.couldnt_add_record"));
    }
  };

  const handleCamera = async () => {
    setBusySource("camera");
    try {
      const permission = await ImagePicker.requestCameraPermissionsAsync();
      if (!permission.granted) return;

      const result = await ImagePicker.launchCameraAsync({
        mediaTypes: ["images"],
        quality: 0.82,
      });

      if (!result.canceled && result.assets?.[0]) {
        const asset = result.assets[0];
        await complete(
          "camera",
          `${mode}-photo-${Date.now()}.jpg`,
          asset.uri,
          resolveUploadContentType({
            name: `${mode}-photo.jpg`,
            uri: asset.uri,
            mimeType: asset.mimeType,
          }),
        );
      }
    } finally {
      setBusySource(null);
    }
  };

  const handleDocument = async () => {
    setBusySource("document");
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: ["application/pdf", "image/*"],
        copyToCacheDirectory: true,
      });

      if (!result.canceled && result.assets?.[0]) {
        const file = result.assets[0];
        await complete(
          "document",
          file.name,
          file.uri,
          resolveUploadContentType({
            name: file.name,
            uri: file.uri,
            mimeType: file.mimeType,
          }),
        );
      }
    } finally {
      setBusySource(null);
    }
  };

  const renderPaymentInput = (
    label: string,
    value: string,
    onChangeText: (value: string) => void,
    placeholder: string,
    options: {
      keyboardType?: "default" | "decimal-pad";
      multiline?: boolean;
    } = {},
  ) => (
    <TextField
      label={label}
      value={value}
      onChangeText={onChangeText}
      placeholder={placeholder}
      keyboardType={options.keyboardType ?? "default"}
      notes={Boolean(options.multiline)}
    />
  );

  const renderPaymentForm = () => (
    <View style={{ gap: 14 }}>
      <InlineOptionPicker
        options={PAYMENT_RECORD_KINDS.map(([kind, labelKey]) => ({
          id: kind,
          label: t(labelKey),
          action: labelKey,
        }))}
        value={paymentKind}
        onChange={(next) => {
          const match = PAYMENT_RECORD_KINDS.find(([kind]) => kind === next);
          if (match) setPaymentKind(match[0]);
        }}
        wrap
        columns={2}
      />

      {paymentKind === "payment" || paymentKind === "receipt" ? (
        <MoonFormField label={t("costs.cost_upload.what_was_it_for")}>
          <InlineOptionPicker
            options={[
              ...MANUAL_PAYMENT_CATEGORY_PRESETS.map((preset) => ({
                id: preset.id,
                label: preset.label,
                action: `manual_payment_category_${preset.id}`,
              })),
              {
                id: "other" as const,
                label: t("costs.cost_upload.category_other"),
                action: "manual_payment_category_other",
              },
            ]}
            value={paymentCategory}
            onChange={(next) =>
              setPaymentCategory(next as ManualPaymentCategory)
            }
            wrap
            columns={3}
          />
        </MoonFormField>
      ) : null}

      <View style={{ flexDirection: "row", gap: 9 }}>
        <View style={{ flex: 1 }}>
          {renderPaymentInput(
            t("costs.cost_upload.amount"),
            paymentAmount,
            setPaymentAmount,
            "$0",
            {
              keyboardType: "decimal-pad",
            },
          )}
        </View>
        <View style={{ flex: 1 }}>
          <DateField
            label={t("costs.cost_upload.date")}
            value={paymentDate}
            onChange={setPaymentDate}
          />
        </View>
      </View>

      {renderPaymentInput(
        t("costs.cost_upload.what_is_this"),
        paymentLabel,
        setPaymentLabel,
        t("costs.cost_upload.record_examples"),
      )}
      {renderPaymentInput(
        t("costs.cost_upload.provider"),
        paymentProvider,
        setPaymentProvider,
        t("costs.cost_upload.provider_examples"),
      )}
      {renderPaymentInput(
        t("costs.cost_upload.notes"),
        paymentNote,
        setPaymentNote,
        t("costs.cost_upload.anything_that_helps_reconcile"),
        { multiline: true },
      )}

      <Pressable
        onPress={() => {
          capturePress(
            pressLabel(
              paymentAttachmentName ??
                (busySource === "document"
                  ? t("costs.cost_upload.opening")
                  : t("costs.cost_upload.attach_supporting_document")),
            ),
            "attach_payment_document",
          );
          void pickPaymentAttachment();
        }}
        disabled={busySource != null}
        style={{
          minHeight: 44,
          borderRadius: 13,
          borderWidth: 1,
          borderColor: semanticColors.border.subtle,
          backgroundColor: semanticColors.surface.glass,
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          paddingHorizontal: 12,
        }}
      >
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 8,
            flex: 1,
            minWidth: 0,
          }}
        >
          <FileText size={15} color={colors.greyBrown} strokeWidth={1.8} />
          <Text
            className="font-dm-sans"
            numberOfLines={1}
            style={{
              flex: 1,
              fontSize: 12,
              fontWeight: "800",
              color: colors.greyBrown,
            }}
          >
            {paymentAttachmentName ??
              (busySource === "document"
                ? t("costs.cost_upload.opening")
                : t("costs.cost_upload.attach_supporting_document"))}
          </Text>
        </View>
        {paymentAttachmentName ? (
          <Check size={14} color={EMBER.colors.green} strokeWidth={2} />
        ) : (
          <UploadCloud size={14} color={colors.greyBrown} strokeWidth={1.8} />
        )}
      </Pressable>
    </View>
  );

  const renderPaymentActions = () => (
    <View style={{ gap: 10 }}>
      {!canSaveManualPayment ? (
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 12,
            lineHeight: 16,
            color: EMBER.colors.textLight,
          }}
        >
          {t("costs.cost_upload.enter_amount_or_attach_file_to_save")}
        </Text>
      ) : null}
      <View style={{ flexDirection: "row", gap: 9 }}>
        <SecondaryButton
          label={t("costs.cost_upload.cancel")}
          action="costs.cost_upload.cancel"
          onPress={handleClose}
          disabled={busySource != null}
          flex={1}
        />
        <PrimaryButton
          label={t("costs.cost_upload.add_record")}
          action="costs.cost_upload.add_record"
          onPress={() => void saveManualPayment()}
          disabled={!canSaveManualPayment || busySource != null}
          loading={busySource != null}
          flex={1}
        />
      </View>
    </View>
  );

  const renderChoice = (
    source: UploadedCostDocument["source"],
    labelKey: string,
    Icon: LucideIcon,
    onPress: () => void,
  ) => (
    <Pressable
      onPress={() => {
        capturePress(pressLabel(t(labelKey)), labelKey);
        onPress();
      }}
      disabled={busySource != null}
      style={{
        ...cardSurface({ variant: "panel" }),
        flex: 1,
        minWidth: 132,
        minHeight: 108,
        alignItems: "center",
        justifyContent: "center",
        gap: 10,
        opacity: busySource && busySource !== source ? 0.55 : 1,
      }}
    >
      <Icon size={28} color={colors.greyBrown} strokeWidth={1.6} />
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 13,
          fontWeight: "700",
          color: EMBER.colors.text,
          textAlign: "center",
        }}
      >
        {busySource === source ? t("costs.cost_upload.opening") : t(labelKey)}
      </Text>
    </Pressable>
  );

  const copy = documentOnly
    ? {
        title: t("costs.cost_upload.add_a_document"),
        body: t("costs.cost_upload.attach_file_to_record"),
      }
    : mode === "quote"
      ? {
          title: t("costs.cost_upload.upload_a_clinic_quote"),
          body: t("costs.cost_upload.louise_reads_quote_lines"),
        }
      : {
          title: t("costs.cost_upload.add_payment_or_claim"),
          body: t("costs.cost_upload.track_before_document_is_ready"),
        };

  const showPaymentForm = mode === "payment" && !documentOnly;

  return (
    <Dialog
      visible
      onClose={() => {
        // The header's close records its own press, so this is the
        // backdrop (or the system back), which has no visible copy.
        capturePress(pressLabel("dismiss_cost_document_upload"));
        handleClose();
      }}
      contentStyle={{
        paddingTop: 18,
        paddingHorizontal: 18,
        paddingBottom: 18,
        gap: 16,
      }}
    >
      <DialogHeader
        title={copy.title}
        subtitle={copy.body}
        trailing={
          <CloseButton
            accessibilityLabel={t("learn.article_actions.close_accessibility")}
            action="close_cost_document_upload"
            onPress={() => handleClose()}
          />
        }
      />

      {showPaymentForm ? (
        // The form scrolls when the keyboard or a short screen leaves too
        // little room, and its actions stay below it.
        <ScrollView
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          {renderPaymentForm()}
        </ScrollView>
      ) : (
        <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 10 }}>
          {renderChoice(
            "camera",
            "costs.cost_upload.take_a_photo",
            Camera,
            handleCamera,
          )}
          {renderChoice(
            "document",
            documentOnly
              ? "costs.cost_upload.upload_document"
              : "costs.cost_upload.upload_quote_file",
            FileText,
            handleDocument,
          )}
        </View>
      )}
      {showPaymentForm ? renderPaymentActions() : null}
      {uploadError ? (
        <ActionBanner
          variant="important"
          message={uploadError}
          actionLabel={
            pendingUpload
              ? t("costs.cost_upload.retry_uploading_file")
              : pendingDocument
                ? t("costs.cost_upload.retry_attaching_file")
                : pendingPaymentAttachment
                  ? t("costs.cost_upload.retry_adding_record")
                  : undefined
          }
          actionIcon={RotateCcw}
          actionLoading={busySource != null}
          onAction={() =>
            void (pendingUpload
              ? retryPendingUpload()
              : pendingDocument
                ? retryPendingDocument()
                : saveManualPayment())
          }
        />
      ) : null}
    </Dialog>
  );
}

function AnimatedCurrencyAmount({
  value,
  locale,
  currency,
  color = EMBER.colors.text,
  testID,
}: {
  value: number;
  locale: Locale;
  currency?: "USD" | "EUR";
  color?: string;
  testID?: string;
}) {
  const [displayValue, setDisplayValue] = useState(value);
  const displayValueRef = useRef(value);
  const frameRef = useRef<number | null>(null);

  useEffect(() => {
    const startValue = displayValueRef.current;
    const delta = value - startValue;

    if (delta === 0) return undefined;

    const duration = 340;
    const startTime = Date.now();

    if (frameRef.current != null) {
      cancelAnimationFrame(frameRef.current);
    }

    const step = () => {
      const progress = Math.min((Date.now() - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const nextValue = Math.round(startValue + delta * eased);

      displayValueRef.current = nextValue;
      setDisplayValue(nextValue);

      if (progress < 1) {
        frameRef.current = requestAnimationFrame(step);
      } else {
        displayValueRef.current = value;
        setDisplayValue(value);
        frameRef.current = null;
      }
    };

    frameRef.current = requestAnimationFrame(step);

    return () => {
      if (frameRef.current != null) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, [value]);

  const formattedValue = formatCurrency(
    displayValue,
    locale,
    currency ? { currency } : undefined,
  );

  return (
    <Text
      testID={testID}
      className="font-dm-mono"
      style={{
        fontSize: formattedValue.length > 14 ? 24 : 29,
        lineHeight: 32,
        fontWeight: "700",
        color,
        textAlign: "left",
      }}
    >
      {formattedValue}
    </Text>
  );
}

function StateSwitch({
  active,
  onChange,
}: {
  active: ConceptCostState;
  onChange: (state: ConceptCostState) => void;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("StateSwitch");
  const options: {
    state: ConceptCostState;
    labelKey: string;
    icon: LucideIcon;
  }[] = [
    {
      state: "estimate",
      labelKey: "costs.money_card.tabs.estimate",
      icon: Calculator,
    },
    {
      state: "clinic-estimate",
      labelKey: "costs.money_card.tabs.quotes",
      icon: FileCheck2,
    },
    {
      state: "payments",
      labelKey: "costs.money_card.tabs.payments",
      icon: Wallet,
    },
  ];

  return (
    <View
      style={{
        flexDirection: "row",
        gap: 6,
        borderRadius: 999,
        backgroundColor: "rgba(42, 37, 34, 0.06)",
        padding: 4,
      }}
    >
      {options.map((option) => {
        const selected = option.state === active;
        const OptionIcon = option.icon;
        const optionColor = selected
          ? EMBER.colors.text
          : EMBER.colors.textLight;

        return (
          <Pressable
            key={option.state}
            onPress={() => {
              capturePress(pressLabel(t(option.labelKey)), option.labelKey);
              onChange(option.state);
            }}
            style={{
              flex: 1,
              minHeight: 28,
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "row",
              gap: 6,
              borderRadius: 999,
              backgroundColor: selected
                ? "rgba(255, 255, 255, 0.78)"
                : "transparent",
              borderWidth: selected ? 1 : 0,
              borderColor: "rgba(42, 37, 34, 0.08)",
            }}
          >
            <OptionIcon size={13} color={optionColor} strokeWidth={1.8} />
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                fontWeight: selected ? "600" : "500",
                color: optionColor,
              }}
            >
              {t(option.labelKey)}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}

function PhysicalStateTabs({
  active,
  onChange,
}: {
  active: ConceptCostState;
  onChange: (state: ConceptCostState) => void;
}) {
  const t = useTranslations();
  return (
    <CostsCardTabs
      active={active}
      onChange={onChange}
      options={[
        {
          value: "estimate",
          label: t("costs.money_card.tabs.estimate"),
          icon: Calculator,
        },
        {
          value: "clinic-estimate",
          label: t("costs.money_card.tabs.quotes"),
          icon: FileCheck2,
        },
        {
          value: "payments",
          label: t("costs.money_card.tabs.payments"),
          icon: Wallet,
        },
      ]}
    />
  );
}

function PaymentProgressBar({
  segments,
  leftToPay,
  beforeInsurance,
  locale,
  currency,
  onPay,
  onAdd,
}: {
  segments: PaymentBreakdownSegment[];
  leftToPay: number;
  /** Her plan's share is not known yet, so this is the total before insurance. */
  beforeInsurance: boolean;
  locale: Locale;
  currency?: "USD" | "EUR";
  onPay: () => void;
  onAdd: () => void;
}) {
  const t = useTranslations();
  const visibleSegments = segments.filter((segment) => segment.value > 0);
  const total = visibleSegments.reduce(
    (sum, segment) => sum + segment.value,
    0,
  );

  return (
    <CardSurface variant="panel" style={{ padding: 14, gap: 12 }}>
      <View
        style={{
          flexDirection: "row",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <View style={{ flex: 1, minWidth: 0, gap: 2 }}>
          <Text
            className="font-dm-mono"
            style={{
              fontSize: 22,
              fontWeight: "800",
              color: colors.greyBrown,
            }}
          >
            {formatCurrency(
              leftToPay,
              locale,
              currency ? { currency } : undefined,
            )}
          </Text>
          <CardSubtitleText style={{ marginTop: 0 }}>
            {beforeInsurance
              ? t("costs.money_card.payments.left_to_pay_before_insurance")
              : t("costs.money_card.payments.left_to_pay_for_scenario")}
          </CardSubtitleText>
        </View>
        <View
          style={{
            flexDirection: "row",
            alignItems: "flex-start",
            gap: 7,
            marginTop: 1,
          }}
        >
          {[
            {
              labelKey: "costs.money_card.payments.financing",
              icon: Landmark,
              iconStrokeWidth: 1.8,
              onPress: onPay,
            },
            {
              labelKey: "costs.money_card.payments.add",
              icon: Plus,
              iconStrokeWidth: 2,
              onPress: onAdd,
            },
          ].map((action) => (
            <CompactActionButton
              key={action.labelKey}
              label={t(action.labelKey)}
              action={action.labelKey}
              onPress={action.onPress}
              variant="secondary"
              size="sm"
              leadingIcon={action.icon}
              iconStrokeWidth={action.iconStrokeWidth}
            />
          ))}
        </View>
      </View>

      <View
        style={{
          height: 10,
          borderRadius: 999,
          overflow: "hidden",
          flexDirection: "row",
          backgroundColor: "rgba(42, 37, 34, 0.075)",
        }}
      >
        {visibleSegments.map((segment) => (
          <View
            key={segment.label}
            style={{
              flexGrow: total > 0 ? Math.max(segment.value / total, 0.04) : 1,
              flexBasis: 0,
              backgroundColor: segment.color,
            }}
          />
        ))}
      </View>

      <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
        {segments.map((segment) => (
          <View
            key={segment.label}
            style={{
              flexGrow: 1,
              flexBasis: 88,
              minWidth: 88,
              gap: 4,
            }}
          >
            <View
              style={{ flexDirection: "row", alignItems: "center", gap: 6 }}
            >
              <View
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: 999,
                  backgroundColor: segment.color,
                }}
              />
              <Text
                className="font-dm-sans"
                numberOfLines={1}
                style={{
                  fontSize: 10,
                  fontWeight: "800",
                  color: EMBER.colors.textLight,
                }}
              >
                {segment.label}
              </Text>
            </View>
            <Text
              className="font-dm-mono"
              style={{
                fontSize: 12,
                fontWeight: "800",
                color: EMBER.colors.text,
              }}
            >
              {formatCurrency(
                segment.value,
                locale,
                currency ? { currency } : undefined,
              )}
            </Text>
          </View>
        ))}
      </View>
    </CardSurface>
  );
}

function buildLocationOptions({
  profileCity,
  profileState,
  profileZipCode,
  profileCountryCode,
  detectedLocation,
  customLocation,
  locationRequestStatus,
  t,
}: {
  profileCity?: string | null;
  profileState?: string | null;
  profileZipCode?: string | null;
  profileCountryCode?: string | null;
  detectedLocation: EstimateAreaOption | null;
  customLocation: EstimateAreaOption | null;
  locationRequestStatus: "idle" | "loading" | "error";
  t: TranslationFunction;
}): ConceptLocationOption[] {
  const profileLocation = buildProfileEstimateAreaOption({
    city: profileCity,
    state: profileState,
    zipCode: profileZipCode,
    countryCode: profileCountryCode,
  });
  const requestLocation: ConceptLocationOption = {
    id: "request-current-location",
    label:
      locationRequestStatus === "loading"
        ? t("costs.money_card.location.locating")
        : t("costs.money_card.location.use_current_location"),
    detail:
      locationRequestStatus === "error"
        ? t("costs.money_card.location.permission_unavailable")
        : t("costs.money_card.location.use_browser_or_device_location"),
    source: "fallback",
    action: "request-current-location",
    estimateLocation: { countryCode: "US" },
  };
  const primaryLocation =
    profileLocation ?? detectedLocation ?? buildFallbackEstimateAreaOption();
  const options = [
    primaryLocation,
    ...(detectedLocation && detectedLocation.id !== primaryLocation.id
      ? [detectedLocation]
      : []),
    ...(customLocation ? [customLocation] : []),
    requestLocation,
    ...US_CITY_ESTIMATE_AREA_OPTIONS,
  ];
  const seen = new Set<string>();

  return options.filter((option) => {
    if (seen.has(option.id)) return false;
    seen.add(option.id);
    return true;
  });
}

function getBrowserCoordinates() {
  return new Promise<{ latitude: number; longitude: number }>(
    (resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("Geolocation is not supported"));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) =>
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          }),
        (error) => reject(error),
        {
          enableHighAccuracy: false,
          maximumAge: 300000,
          timeout: 10000,
        },
      );
    },
  );
}

async function reverseGeocodeWithGoogle({
  latitude,
  longitude,
}: {
  latitude: number;
  longitude: number;
}) {
  const apiKey = process.env.EXPO_PUBLIC_GOOGLE_MAPS_API_KEY;
  if (!apiKey) return null;

  const response = await fetch(
    `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${apiKey}`,
  );
  if (!response.ok) return null;
  const data = (await response.json()) as {
    results?: {
      address_components?: {
        long_name: string;
        short_name: string;
        types: string[];
      }[];
    }[];
  };
  const components = data.results?.[0]?.address_components ?? [];
  const findComponent = (type: string, field: "long_name" | "short_name") =>
    components.find((component) => component.types.includes(type))?.[field] ??
    null;

  return {
    city:
      findComponent("locality", "long_name") ??
      findComponent("postal_town", "long_name") ??
      findComponent("administrative_area_level_2", "long_name"),
    state: findComponent("administrative_area_level_1", "short_name"),
    countryCode: findComponent("country", "short_name"),
  };
}

async function requestCurrentEstimateAreaOption() {
  if (Platform.OS === "web") {
    const coordinates = await getBrowserCoordinates();
    const geocode = await reverseGeocodeWithGoogle(coordinates);

    return buildDetectedEstimateAreaOption({
      ...coordinates,
      city: geocode?.city,
      state: geocode?.state,
      countryCode: geocode?.countryCode,
    });
  }

  const { status } = await ExpoLocation.requestForegroundPermissionsAsync();
  if (status !== "granted") {
    throw new Error("Location permission denied");
  }
  const position = await ExpoLocation.getCurrentPositionAsync({
    accuracy: ExpoLocation.Accuracy.Balanced,
  });
  const geocode = await ExpoLocation.reverseGeocodeAsync({
    latitude: position.coords.latitude,
    longitude: position.coords.longitude,
  });
  const firstResult = geocode[0];

  return buildDetectedEstimateAreaOption({
    latitude: position.coords.latitude,
    longitude: position.coords.longitude,
    city: firstResult?.city ?? firstResult?.subregion,
    state: firstResult?.region,
    countryCode: firstResult?.isoCountryCode ?? "US",
  });
}

function InsightBox({
  icon: Icon,
  title,
  body,
}: {
  icon: LucideIcon;
  title: string;
  body: string;
}) {
  return (
    <View style={{ gap: 7 }}>
      <View style={{ flexDirection: "row", alignItems: "center", gap: 7 }}>
        <Icon size={14} color={EMBER.colors.textLight} strokeWidth={1.6} />
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 13,
            fontWeight: "700",
            color: EMBER.colors.text,
          }}
        >
          {title}
        </Text>
      </View>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 12,
          lineHeight: 18,
          color: EMBER.colors.textMid,
        }}
      >
        {body}
      </Text>
    </View>
  );
}

function EstimateStateIcon({ selected }: { selected: boolean }) {
  return (
    <View
      style={{
        width: 20,
        height: 20,
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {selected ? (
        <CheckCircle2 size={16} color={EMBER.colors.text} strokeWidth={1.9} />
      ) : (
        <Plus size={15} color={EMBER.colors.textLight} strokeWidth={2} />
      )}
    </View>
  );
}

function formatCountryName(code: string, locale: Locale): string {
  try {
    const names = new Intl.DisplayNames([locale], { type: "region" });
    return names.of(code) ?? code;
  } catch {
    return code;
  }
}

function AvailabilityLine({
  lineId,
  availability,
  locale,
  t,
}: {
  lineId: EstimateLineId;
  availability: CostAvailabilityPolicy;
  locale: Locale;
  t: ReturnType<typeof useTranslations>;
}) {
  const label = t(
    `costs.money_card.scenario.availability_line_labels.${lineId}`,
  );
  const reasonKey: CostRestrictionReason =
    availability.restrictionReasons?.[0] ?? "unknown";
  const reason = t(
    `costs.money_card.scenario.restriction_reasons.${reasonKey}`,
  );
  const destinations = availability.crossBorderCommonDestinations ?? [];
  const destinationNames = destinations
    .map((code) => formatCountryName(code, locale))
    .join(", ");

  let text: string;
  switch (availability.status) {
    case "funded":
      text = t("costs.money_card.scenario.funded_line", {
        params: { line: label },
      });
      break;
    case "partially-funded":
      text = t("costs.money_card.scenario.partially_funded_line", {
        params: { line: label },
      });
      break;
    case "restricted":
      text = t("costs.money_card.scenario.restricted_line", {
        params: { line: label, reason },
      });
      break;
    case "not-available":
    default:
      text = t("costs.money_card.scenario.not_available_line", {
        params: { line: label, reason },
      });
      break;
  }

  if (availability.fundedCyclesLimit != null) {
    text += t("costs.money_card.scenario.line_cycle_limit_suffix", {
      params: { count: String(availability.fundedCyclesLimit) },
    });
  }
  if (availability.expectedWaitMonths) {
    text += t("costs.money_card.scenario.line_wait_suffix", {
      params: {
        min: String(availability.expectedWaitMonths.min),
        max: String(availability.expectedWaitMonths.max),
      },
    });
  }
  if (destinations.length > 0) {
    text += t("costs.money_card.scenario.line_destinations_suffix", {
      params: { destinations: destinationNames },
    });
  }

  const isPositive =
    availability.status === "funded" ||
    availability.status === "partially-funded";
  const Icon = isPositive ? Check : X;
  const iconColor = isPositive ? EMBER.colors.textMid : EMBER.colors.coral;

  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "flex-start",
        gap: 8,
      }}
    >
      <View style={{ paddingTop: 2 }}>
        <Icon size={14} color={iconColor} strokeWidth={1.9} />
      </View>
      <Text
        className="font-dm-sans"
        style={{
          flex: 1,
          fontSize: 12,
          lineHeight: 18,
          color: EMBER.colors.textMid,
        }}
      >
        {text}
      </Text>
    </View>
  );
}

function getLineAmount(
  line: EstimateScenarioLine,
  params: {
    cycleCount: number;
    embryoCount: number;
    storageYears: number;
    transferCount: number;
  },
) {
  if (line.multiplier === "cycle") return line.amount * params.cycleCount;
  if (line.multiplier === "embryo") return line.amount * params.embryoCount;
  if (line.multiplier === "storage-year") {
    return line.amount * params.storageYears;
  }
  if (line.multiplier === "transfer") return line.amount * params.transferCount;
  return line.amount;
}

function getStatusLabel(status: EstimateLineStatus, t: TranslationFunction) {
  if (status === "likely-included") {
    return t("costs.money_card.line_status.confirm_bundle");
  }
  if (status === "often-separate") {
    return t("costs.money_card.line_status.often_billed_separately");
  }
  return t("costs.money_card.line_status.optional_add_on");
}

function getStatusColor(status: EstimateLineStatus) {
  if (status === "likely-included") return EMBER.colors.textFaint;
  if (status === "often-separate") return colors.greyBrown;
  return EMBER.colors.textMid;
}

function uniqueItems(items: string[]) {
  return Array.from(new Set(items.filter(Boolean)));
}

function formatScenarioList(items: string[], t: TranslationFunction) {
  const unique = uniqueItems(items);
  if (unique.length === 0) return "";
  if (unique.length === 1) return unique[0]!;
  if (unique.length === 2) {
    return t("costs.money_card.scenario_summary.list_of_two", {
      params: { first: unique[0]!, second: unique[1]! },
    });
  }
  return t("costs.money_card.scenario_summary.list_of_many", {
    params: {
      items: unique.slice(0, -1).join(", "),
      last: unique[unique.length - 1]!,
    },
  });
}

function limitScenarioItems(
  items: string[],
  t: TranslationFunction,
  maxItems = 4,
) {
  const unique = uniqueItems(items);
  if (unique.length <= maxItems) return unique;
  return [
    ...unique.slice(0, maxItems),
    t("costs.money_card.scenario_summary.other_add_ons"),
  ];
}

function sentenceCase(value: string) {
  if (!value) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
}

// `countKey` names a `_one` / `_other` pair, so "one cycle" and "2 cycles"
// read naturally in each language.
function formatScenarioCount(
  count: number,
  countKey: string,
  t: TranslationFunction,
) {
  return t(`${countKey}_${count === 1 ? "one" : "other"}`, {
    params: { count: String(count) },
  });
}

function getScenarioAreaPhrase(
  location: ConceptLocationOption,
  locationIsSpecific: boolean,
  t: TranslationFunction,
) {
  // Only claim "in this location" when the catalog actually prices this place
  // below the country level. Otherwise it's a national average regardless of
  // the picked label (the catalog is country-only today), so say so. Non-US
  // locations name the country rather than defaulting to the US copy.
  const countryCode = location.estimateLocation.countryCode;
  if (!locationIsSpecific) {
    if (countryCode !== "US" && location.estimateAreaLabel) {
      return t(
        "costs.money_card.scenario_summary.area.using_national_averages_in_country",
        { params: { country: location.estimateAreaLabel } },
      );
    }
    return t("costs.money_card.scenario_summary.area.using_us_averages");
  }
  const label = location.label.trim();
  if (!label || label === "Your area") {
    if (countryCode !== "US" && location.estimateAreaLabel) {
      return t(
        "costs.money_card.scenario_summary.area.using_national_averages_in_country",
        { params: { country: location.estimateAreaLabel } },
      );
    }
    return t("costs.money_card.scenario_summary.area.using_us_averages");
  }
  // Custom locations priced through a nearest supported area shouldn't claim
  // to be "in" the place the person asked for.
  if (location.estimateAreaLabel) {
    return t("costs.money_card.scenario_summary.area.near_estimate_area", {
      params: { area: location.estimateAreaLabel },
    });
  }
  return t("costs.money_card.scenario_summary.area.in_this_location");
}

// Human-readable label for the location the estimate actually uses, for
// analytics comparison against the location the person asked for.
function getEstimateAreaAnalyticsLabel(option: ConceptLocationOption) {
  if (option.estimateAreaLabel) return option.estimateAreaLabel;
  const { city, stateCode, countryCode } = option.estimateLocation;
  if (city && stateCode) return `${city}, ${stateCode}`;
  if (stateCode) return getUsStateName(stateCode) ?? stateCode;
  return countryCode;
}

// Notice shown under the estimate for a searched location, naming both the
// requested place and the estimate location actually used. Derived from the
// selected option so it re-appears when the person re-selects a saved custom
// location. Returns null for supported/known selections that need no notice.
function deriveLocationNotice(
  option: ConceptLocationOption,
  t: TranslationFunction,
): string | null {
  if (option.source !== "custom" || !option.estimateAreaLabel) return null;
  const params = { place: option.label, area: option.estimateAreaLabel };
  if (option.estimateLocation.stateCode) {
    return t("costs.money_card.location.showing_closest_available_estimate", {
      params,
    });
  }
  return t("costs.money_card.location.showing_national_estimate", { params });
}

function getMedicationIntensityPhrase(
  medicationIntensity: "low" | "typical" | "high",
  treatment: TreatmentType,
  t: TranslationFunction,
) {
  const medication = t(
    treatment === "fet"
      ? "costs.money_card.scenario_summary.medications.transfer"
      : treatment === "iui"
        ? "costs.money_card.scenario_summary.medications.iui"
        : treatment === "ovulation_induction"
          ? "costs.money_card.scenario_summary.medications.ovulation"
          : "costs.money_card.scenario_summary.medications.stimulation",
  );
  const params = { params: { medication } };
  if (medicationIntensity === "typical") {
    return t(
      "costs.money_card.scenario_summary.medication_intensity.typical",
      params,
    );
  }
  if (medicationIntensity === "low") {
    return t(
      "costs.money_card.scenario_summary.medication_intensity.lower",
      params,
    );
  }
  return t(
    "costs.money_card.scenario_summary.medication_intensity.higher",
    params,
  );
}

function getScenarioLineShortLabel(
  line: EstimateScenarioLine,
  t: TranslationFunction,
) {
  const name = (key: string) =>
    t(`costs.money_card.scenario_summary.line_names.${key}`);
  switch (line.key) {
    case "consult":
      return name("consult");
    case "female-workup":
    case "male-workup":
      return name("pre_cycle_labs");
    case "uterine-cavity":
      return name("cavity_check");
    case "anesthesia":
      return name("retrieval_anesthesia");
    case "medications":
      return name("medications");
    case "icsi":
      return "ICSI";
    case "pgta":
      return "PGT-A";
    case "pgtm-sr":
      return "PGT-M/SR";
    case "pgtp":
      return "PGT-P";
    case "assisted-hatching":
      return name("assisted_hatching");
    case "sperm-wash":
      return name("sperm_prep");
    case "donor-sperm":
      return name("donor_sperm");
    case "donor-eggs":
      return name("donor_eggs");
    case "donor-embryo":
      return name("donor_embryo");
    case "known-donor-coordination":
      return name("known_donor_steps");
    case "storage":
      return name("storage");
    case "specimen-transfer":
      return name("shipping");
    case "additional-fet":
      return name("another_transfer");
    case "transfer-meds":
      return name("transfer_medications");
    case "extra-cycle-buffer":
      return name("another_cycle");
    default:
      return line.label.toLowerCase();
  }
}

function getSeparateScenarioGroup(
  section: EstimateScenarioSection,
  line: EstimateScenarioLine,
  t: TranslationFunction,
) {
  if (section.key === "before-cycle") {
    return t("costs.money_card.scenario_summary.separate_costs.testing");
  }
  if (section.key === "cycle" && line.key === "anesthesia") {
    return t("costs.money_card.scenario_summary.separate_costs.anesthesia");
  }
  if (section.key === "medications") {
    return t("costs.money_card.scenario_summary.line_names.medications");
  }
  if (section.key === "lab-add-ons") return getScenarioLineShortLabel(line, t);
  if (section.key === "storage") {
    return t("costs.money_card.scenario_summary.line_names.storage");
  }
  if (section.key === "transfer-path") {
    return t("costs.money_card.scenario_summary.separate_costs.transfer_costs");
  }
  return section.title.toLowerCase();
}

function buildScenarioNarrative({
  treatment,
  location,
  locationIsSpecific,
  sections,
  selectedLineKeys,
  cycleCount,
  medicationIntensity,
  embryoCount,
  storageYears,
  transferCount,
  t,
}: {
  treatment: TreatmentType;
  location: ConceptLocationOption;
  locationIsSpecific: boolean;
  sections: EstimateScenarioSection[];
  selectedLineKeys: string[];
  cycleCount: number;
  medicationIntensity: "low" | "typical" | "high";
  embryoCount: number;
  storageYears: number;
  transferCount: number;
  t: TranslationFunction;
}) {
  const selectedKeySet = new Set(selectedLineKeys);
  const selectedItems = sections.flatMap((section) =>
    section.lines
      .filter((line) => selectedKeySet.has(line.key))
      .map((line) => ({ section, line })),
  );
  const selectedKeys = new Set(selectedItems.map(({ line }) => line.key));
  const hasSelectedLine = (key: string) => selectedKeys.has(key);

  const scenarioParts: string[] = [];
  const hasCyclePackage = selectedItems.some(({ line }) =>
    [
      "egg-freezing-cycle",
      "embryo-freezing-cycle",
      "ivf-cycle",
      "iui-cycle",
      "oi-cycle",
      "sperm-freezing-cycle",
    ].includes(line.key),
  );
  const hasTransferPackage =
    hasSelectedLine("fet-cycle") || hasSelectedLine("additional-fet");
  const medicationPhrase = hasSelectedLine("medications")
    ? getMedicationIntensityPhrase(medicationIntensity, treatment, t)
    : null;

  if (hasCyclePackage) {
    const cyclePhrase = formatScenarioCount(
      cycleCount,
      "costs.money_card.scenario_summary.cycle_count",
      t,
    );
    scenarioParts.push(cyclePhrase);
    if (medicationPhrase) {
      scenarioParts.push(medicationPhrase);
    }
  } else if (medicationPhrase) {
    scenarioParts.push(medicationPhrase);
  }
  if (hasTransferPackage) {
    scenarioParts.push(
      formatScenarioCount(
        transferCount,
        "costs.money_card.scenario_summary.transfer_count",
        t,
      ),
    );
  }
  if (hasSelectedLine("pgta") || hasSelectedLine("pgtp")) {
    scenarioParts.push(
      formatScenarioCount(
        embryoCount,
        "costs.money_card.scenario_summary.embryos_for_pgt",
        t,
      ),
    );
  }
  if (hasSelectedLine("storage")) {
    scenarioParts.push(
      formatScenarioCount(
        storageYears,
        "costs.money_card.scenario_summary.years_of_storage",
        t,
      ),
    );
  }
  if (
    hasSelectedLine("donor-sperm") ||
    hasSelectedLine("donor-eggs") ||
    hasSelectedLine("donor-embryo") ||
    hasSelectedLine("known-donor-coordination")
  ) {
    scenarioParts.push(
      t("costs.money_card.scenario_summary.donor_related_costs"),
    );
  }

  const separateGroups = selectedItems
    .filter(
      ({ line }) =>
        line.status === "often-separate" &&
        line.key !== "medications" &&
        line.key !== "storage",
    )
    .map(({ section, line }) => getSeparateScenarioGroup(section, line, t));
  const selectedOptionalItems = selectedItems
    .filter(({ line }) => line.status === "optional")
    .map(({ line }) => getScenarioLineShortLabel(line, t));
  const unselectedOptionalItems = sections.flatMap((section) =>
    section.lines
      .filter(
        (line) => line.status === "optional" && !selectedKeySet.has(line.key),
      )
      .map((line) => getScenarioLineShortLabel(line, t)),
  );

  const firstSentence = t(
    "costs.money_card.scenario_summary.scenario_assumes",
    {
      params: {
        treatment: t(TREATMENT_IN_SENTENCE_KEYS[treatment]),
        area: getScenarioAreaPhrase(location, locationIsSpecific, t),
        items:
          scenarioParts.length > 0
            ? formatScenarioList(scenarioParts, t)
            : t("costs.money_card.scenario_summary.the_selected_items"),
      },
    },
  );

  const includedItems = limitScenarioItems(
    [...separateGroups, ...selectedOptionalItems],
    t,
    3,
  );
  const excludedOptional = limitScenarioItems(unselectedOptionalItems, t, 2);
  // Each sentence opens with its list in English ("Storage is included."), so
  // the whole sentence is capitalized rather than the list alone.
  const itemsSentence = (key: string, items: string[]) =>
    sentenceCase(
      t(
        `costs.money_card.scenario_summary.${key}_${
          items.length === 1 ? "one" : "other"
        }`,
        { params: { items: formatScenarioList(items, t) } },
      ),
    );

  if (includedItems.length > 0 && excludedOptional.length > 0) {
    return `${firstSentence} ${itemsSentence(
      "included",
      includedItems,
    )} ${itemsSentence("not_included_brief", excludedOptional)}`;
  }

  if (includedItems.length > 0) {
    return `${firstSentence} ${itemsSentence("included_too", includedItems)}`;
  }

  if (excludedOptional.length > 0) {
    return `${firstSentence} ${itemsSentence(
      "not_included",
      excludedOptional,
    )}`;
  }

  return `${firstSentence} ${t(
    "costs.money_card.scenario_summary.adjust_items_as_plan_changes",
  )}`;
}

function getCostStateSummary(params: {
  state: ConceptCostState;
  treatmentLabel: string;
  estimateTotal: string;
  patientEstimate: string | null;
  quoteTotal: string;
  quoteCount: number;
  comparableQuoteCount: number;
  paymentDue: string;
  hasInsurance: boolean;
  coverageVerified: boolean;
  insuranceLabel: string;
  clinicCount: number;
  verifiedClinicCount: number;
  isPublicCoverageRegion?: boolean;
  t: TranslationFunction;
}): CostStateSummary {
  const { t } = params;
  if (params.state === "clinic-estimate") {
    const primaryActionKey =
      params.quoteCount > 0
        ? "costs.money_card.actions.compare_quotes"
        : "costs.money_card.actions.review_insurance";
    const secondaryActionKey = "costs.money_card.actions.financing_options";
    return {
      eyebrow: "Clinic estimate workspace",
      title: "Compare quotes before money moves",
      amount: params.quoteTotal,
      amountLabel:
        params.comparableQuoteCount > 1
          ? t(
              "costs.money_card.amount_context.lowest_uploaded_quote_before_insurance",
            )
          : params.comparableQuoteCount === 1
            ? t(
                "costs.money_card.amount_context.uploaded_quote_before_insurance",
              )
            : params.quoteCount > 0
              ? t("costs.money_card.amount_context.uploaded_quote_needs_review")
              : t("costs.money_card.amount_context.no_clinic_quotes_uploaded"),
      description:
        "Once a clinic shares an official estimate, this view compares included lines, likely surprise charges, insurance fit, and financing paths.",
      badge: params.quoteCount > 0 ? "Clinic quote" : "Awaiting quote",
      badgeTone: "quote",
      rows: [
        {
          label: "Quotes on file",
          value:
            params.quoteCount > 0
              ? `${params.quoteCount} uploaded`
              : "None uploaded",
          detail:
            params.quoteCount > 0
              ? "Compare itemized lines, exclusions, and patient responsibility."
              : "Upload a clinic quote or request one from a selected clinic.",
        },
        {
          label: "Insurance read",
          value: params.insuranceLabel,
          detail: params.coverageVerified
            ? "Verified plan details can be applied to clinic line items."
            : "A benefits check is still needed before this is plan-aware.",
        },
        {
          label: "Decision point",
          value: params.quoteCount > 0 ? "Compare out-of-pocket" : "Get quote",
          detail:
            "Ask clinics to itemize cycle fees, meds, storage, labs, anesthesia, and add-ons.",
        },
      ],
      primaryAction: t(primaryActionKey),
      primaryActionKey,
      secondaryAction: t(secondaryActionKey),
      secondaryActionKey,
      primaryRoute: params.quoteCount > 0 ? undefined : "Insurance",
      secondaryRoute: "Financing",
    };
  }

  if (params.state === "payments") {
    const primaryActionKey = "costs.money_card.actions.review_insurance";
    const secondaryActionKey = "costs.money_card.actions.tax_tracker";
    return {
      eyebrow: "Payments and claims",
      title: "Track what is billed, covered, and due",
      amount: params.paymentDue,
      amountLabel: t(
        "costs.money_card.amount_context.left_to_pay_after_recorded_payments",
      ),
      description:
        "After treatment starts, this view tracks pre-cycle payments, deposits, claims, receipts, reimbursements, and what needs attention next.",
      badge: "Active care",
      badgeTone: "payments",
      rows: [
        {
          label: "Clinic balance",
          value: "Needs statement",
          detail:
            "Connect or upload clinic statements to separate charges from payments.",
        },
        {
          label: "Insurance claims",
          value: params.coverageVerified ? "Ready to match" : "Pending plan",
          detail: params.coverageVerified
            ? "EOBs can be matched against quote and payment lines."
            : "Verified coverage unlocks claim-level reconciliation.",
        },
        {
          label: "Receipts",
          value: "Tax-ready folder",
          detail: "Keep medication, storage, lab, and travel receipts grouped.",
        },
      ],
      primaryAction: t(primaryActionKey),
      primaryActionKey,
      secondaryAction: t(secondaryActionKey),
      secondaryActionKey,
      primaryRoute: "Insurance",
      secondaryRoute: "TaxDeductions",
    };
  }

  const primaryActionKey = params.hasInsurance
    ? "costs.money_card.actions.review_insurance"
    : "costs.money_card.actions.check_coverage";
  const secondaryActionKey = "costs.money_card.actions.financing_options";
  return {
    eyebrow: "Planning estimate",
    title: `Start with a ${params.treatmentLabel} ballpark`,
    amount: params.patientEstimate ?? params.estimateTotal,
    amountLabel: params.isPublicCoverageRegion
      ? t("costs.money_card.amount_context.patient_paid_with_public_coverage")
      : params.patientEstimate
        ? t("costs.money_card.amount_context.estimated_out_of_pocket")
        : t("costs.money_card.amount_context.estimated_before_insurance"),
    description:
      "Use this estimate while exploring. It updates as coverage is verified, clinics are compared, and itemized quotes arrive.",
    badge: params.patientEstimate ? "Plan-aware estimate" : "Estimate",
    badgeTone: "estimate",
    rows: [
      {
        label: "Treatment path",
        value: params.treatmentLabel,
        detail:
          "Includes common diagnostics, medications, and one year storage.",
      },
      {
        label: "Insurance status",
        value: params.insuranceLabel,
        detail: params.hasInsurance
          ? "Coverage verification controls whether this can show likely out-of-pocket."
          : "Add insurance to move from total charges toward responsibility.",
      },
      {
        label: "Next best step",
        value: params.hasInsurance ? "Compare clinics" : "Check coverage",
        detail: "Clinic quotes can replace public estimates.",
      },
    ],
    primaryAction: t(primaryActionKey),
    primaryActionKey,
    secondaryAction: t(secondaryActionKey),
    secondaryActionKey,
    primaryRoute: params.hasInsurance ? "Insurance" : undefined,
    secondaryRoute: "Financing",
  };
}

function AdaptiveCostStatusCard({
  state,
  onStateChange,
  initialTreatmentType,
  diagnosticProfile,
  locale,
  profileCity,
  profileState,
  profileZipCode,
  profileCountryCode,
  hasInsurance,
  coverageVerified,
  verificationStatus,
  insuranceLabel,
  fertilityCoverage,
  primaryPolicy,
  clinicCount,
  verifiedClinicCount,
  financialPlanLabel,
  onAddInsurance,
  onNavigate,
  onViewAllPayments,
  onOpenChat,
  onCompareQuotes,
  initialUploadMode,
  onUploadModeConsumed,
  actionButtonStyle = "default",
  topControlsVariant = "pills",
  hideInternalStateSwitch = false,
}: {
  state: ConceptCostState;
  onStateChange: (state: ConceptCostState) => void;
  initialTreatmentType: TreatmentType;
  diagnosticProfile: DiagnosticProfile;
  locale: Locale;
  profileCity?: string | null;
  profileState?: string | null;
  profileZipCode?: string | null;
  profileCountryCode?: string | null;
  hasInsurance: boolean;
  coverageVerified: boolean;
  verificationStatus: "unverified" | "pending" | "verified";
  insuranceLabel: string;
  fertilityCoverage: FertilityCoverage | null;
  primaryPolicy: InsurancePolicy | null;
  clinicCount: number;
  verifiedClinicCount: number;
  financialPlanLabel: string | null;
  onAddInsurance: () => void;
  onNavigate: (
    route: CostsOverviewRouteName,
    params?: CostsOverviewRouteParams,
  ) => void;
  onViewAllPayments: (params: CostPaymentsRouteParams) => void;
  onOpenChat: (record: CostPaymentRecordParam) => void;
  onCompareQuotes: (context: QuoteComparisonContext) => void;
  initialUploadMode?: CostUploadMode;
  onUploadModeConsumed?: () => void;
  actionButtonStyle?: "default" | "care";
  topControlsVariant?: "pills" | "bar";
  hideInternalStateSwitch?: boolean;
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("AdaptiveCostStatusCard");
  const [selectedTreatment, setSelectedTreatment] =
    useState<TreatmentType>(initialTreatmentType);
  const [selectedLocationId, setSelectedLocationId] = useState("profile");
  useEffect(() => {
    setSelectedTreatment(initialTreatmentType);
  }, [initialTreatmentType]);

  // The last searched location that resolved to a nearest supported area or a
  // national estimate. Stays in the option list after selecting something else
  // so the entered location is still there when the person comes back to edit.
  const [customLocation, setCustomLocation] =
    useState<ConceptLocationOption | null>(null);
  const [locationSearchHint, setLocationSearchHint] = useState<string | null>(
    null,
  );
  // Query for the searchable location control; predictions come from the
  // shared usePlacesPredictions hook below.
  const [locationQuery, setLocationQuery] = useState("");
  const [selectedLineKeys, setSelectedLineKeys] = useState<string[]>([]);
  const [cycleCount, setCycleCount] = useState(1);
  const [medicationIntensity, setMedicationIntensity] = useState<
    "low" | "typical" | "high"
  >("typical");
  const [embryoCount, setEmbryoCount] = useState(4);
  const [storageYears, setStorageYears] = useState(1);
  const [transferCount, setTransferCount] = useState(1);
  const [showBreakdown, setShowBreakdown] = useState(false);
  // In read-only (public-system) regions, each section inside the scenario
  // builder starts collapsed. Clicking a section header toggles whether its
  // line items render.
  const [expandedSectionKeys, setExpandedSectionKeys] = useState<Set<string>>(
    () => new Set(),
  );
  const toggleSectionExpanded = (sectionKey: string) => {
    setExpandedSectionKeys((prev) => {
      const next = new Set(prev);
      if (next.has(sectionKey)) {
        next.delete(sectionKey);
      } else {
        next.add(sectionKey);
      }
      return next;
    });
  };
  const [showInfo, setShowInfo] = useState(false);
  const [detectedLocation, setDetectedLocation] =
    useState<EstimateAreaOption | null>(null);
  const [openAssumptionControl, setOpenAssumptionControl] = useState<
    "treatment" | "location" | null
  >(null);
  const [locationRequestStatus, setLocationRequestStatus] = useState<
    "idle" | "loading" | "error"
  >("idle");
  const [uploadModalMode, setUploadModalMode] = useState<CostUploadMode | null>(
    null,
  );
  // In-flight OCR extraction state for uploaded quotes, keyed by clinic-quote id.
  // Completed extractions live on the session-only clinic-quote record.
  const transfer = useDocumentSourceTransfer();
  const { capture: captureEstimateLocationIntent } =
    useEstimateLocationIntent();
  const estimateFlow = useFlowAnalytics("coverage_estimate");
  // An estimate is "completed" once it is priced for somewhere the person
  // chose, not the moment a number appears — everyone lands on a number from
  // the profile default, so counting that would flatten the funnel to 100%.
  const estimateLocationChosen = useRef(false);
  // Choosing a location is both the domain fact the estimate team reads
  // (`cost_estimate_location_requested`) and the step the funnel turns on, so
  // the two are recorded from one place rather than drifting apart.
  const captureLocationIntent = useCallback(
    (
      event: Parameters<
        ReturnType<typeof useEstimateLocationIntent>["capture"]
      >[0],
    ) => {
      captureEstimateLocationIntent(event);
      estimateFlow.stepSubmitted("location", {
        selection_source: event.selectionSource,
        match_type: event.matchType,
      });
      if (event.matchType === "unsupported") return;
      if (estimateLocationChosen.current) return;
      estimateLocationChosen.current = true;
      estimateFlow.completed({
        selection_source: event.selectionSource,
        match_type: event.matchType,
      });
    },
    [captureEstimateLocationIntent, estimateFlow],
  );
  const [quoteExtractions, setQuoteExtractions] = useState<
    Record<
      string,
      {
        status: "extracting" | "done" | "error";
        data?: QuoteOCRResult;
        error?: string;
      }
    >
  >({});
  const paymentRecordsQuery = usePaymentRecords();
  const clinicQuotesQuery = useClinicQuotes();
  const createPaymentRecordMutation = useCreatePaymentRecord();
  const deletePaymentRecordMutation = useDeletePaymentRecord();
  const updatePaymentRecordMutation = useUpdatePaymentRecord();
  const createClinicQuoteMutation = useCreateClinicQuote();
  const deleteClinicQuoteMutation = useDeleteClinicQuote();
  const updateClinicQuoteMutation = useUpdateClinicQuote();
  const attemptedQuoteExtractionsRef = useRef<Set<string>>(new Set());
  const [expandedQuoteIds, setExpandedQuoteIds] = useState<string[]>([]);
  const [quoteDeleteTargetId, setQuoteDeleteTargetId] = useState<string | null>(
    null,
  );
  const [quoteDeleteError, setQuoteDeleteError] = useState<string | null>(null);
  const [selectedPaymentRecordId, setSelectedPaymentRecordId] = useState<
    string | null
  >(null);
  const [attachmentRecordId, setAttachmentRecordId] = useState<string | null>(
    null,
  );
  const [deletedPaymentRecordIds, setDeletedPaymentRecordIds] = useState<
    string[]
  >([]);
  const [paymentDocumentAttachments, setPaymentDocumentAttachments] = useState<
    Record<string, PaymentDocumentLine[]>
  >({});
  const [editedPaymentRecords, setEditedPaymentRecords] = useState<
    Record<string, PaymentRecord>
  >({});
  const previousStateRef = useRef(state);

  const locationOptions = useMemo(
    () =>
      buildLocationOptions({
        profileCity,
        profileState,
        profileZipCode,
        profileCountryCode,
        detectedLocation,
        customLocation,
        locationRequestStatus,
        t,
      }),
    [
      customLocation,
      detectedLocation,
      locationRequestStatus,
      profileCity,
      profileCountryCode,
      profileState,
      profileZipCode,
      t,
    ],
  );
  const selectedLocation =
    locationOptions.find((option) => option.id === selectedLocationId) ??
    locationOptions[0]!;

  useEffect(() => {
    if (!locationOptions.some((option) => option.id === selectedLocationId)) {
      setSelectedLocationId(locationOptions[0]?.id ?? "fallback");
    }
  }, [locationOptions, selectedLocationId]);

  useEffect(() => {
    if (!initialUploadMode) return;
    setUploadModalMode(initialUploadMode);
    onUploadModeConsumed?.();
  }, [initialUploadMode, onUploadModeConsumed]);

  useEffect(() => {
    const fixture = getDevCostsLocationFixture();
    if (!fixture) return;
    const resolution = resolvePlaceEstimateArea(fixture);
    if (resolution.status === "exact") {
      setSelectedLocationId(resolution.option.id);
    } else if (
      resolution.status === "nearest" ||
      resolution.status === "country"
    ) {
      setCustomLocation(resolution.option);
      setSelectedLocationId(resolution.option.id);
    } else {
      setLocationSearchHint(resolution.hint);
    }
  }, []);

  useEffect(() => {
    if (previousStateRef.current === state) return;

    setOpenAssumptionControl(null);
    setShowBreakdown(false);
    setShowInfo(false);
    setUploadModalMode(null);
    setExpandedQuoteIds([]);
    setSelectedPaymentRecordId(null);
    previousStateRef.current = state;
  }, [state]);

  // Scenario builder soft-returns null for unpriced lines and skips entirely-
  // empty sections, so this is safe for any supported country. FR resolves
  // most lines to €0 (publicly funded via Assurance Maladie).
  const estimateSections = useMemo(
    () =>
      buildEstimateScenarioSections({
        treatment: selectedTreatment,
        diagnosticProfile,
        medicationIntensity,
        estimateLocation: selectedLocation.estimateLocation,
      }),
    [
      selectedTreatment,
      diagnosticProfile,
      medicationIntensity,
      selectedLocation.estimateLocation,
    ],
  );
  const estimateCurrency: "USD" | "EUR" =
    selectedLocation.estimateLocation.countryCode === "FR" ? "EUR" : "USD";
  // For public-system markets like France, toggling €0 lines has no financial
  // meaning. Render the breakdown as a read-only coverage list: no checkboxes,
  // no toggle presses, no "X/Y selected" counter.
  const isReadOnlyRegion =
    selectedLocation.estimateLocation.countryCode === "FR";

  // For non-US locations, prefer the catalog's availability notes
  // ("Covered by Assurance Maladie, up to 4 cycles under 43") over the
  // hardcoded US clinic tip that ships in each scenario line. Line keys
  // mostly map 1:1 to EstimateLineIds; the "medications" key doesn't, so
  // resolve it to the right medication line via getMedicationLineId.
  const getLineCoverageDetail = (line: { key: string }): string | null => {
    if (selectedLocation.estimateLocation.countryCode === "US") return null;
    const lineId: EstimateLineId | null =
      line.key === "medications"
        ? getMedicationLineId(selectedTreatment)
        : (line.key as EstimateLineId);
    if (!lineId) return null;
    const availability = getLineAvailability(
      lineId,
      selectedLocation.estimateLocation,
    );
    return availability?.notes ?? null;
  };

  // Line items whose availability policy for the current location is anything
  // other than "available" — surfaced as "Available in your region" (funded /
  // partially funded) and "Not available in your region" (restricted / not
  // available) notes so patients see both the real public pathway and the
  // things that drive cross-border care.
  const regionAvailability = useMemo(() => {
    const candidates: EstimateLineId[] = [
      "consult",
      "ivf-cycle",
      "iui-cycle",
      "storage",
      "pgta",
      "pgtp",
      "pgtm-sr",
      "donor-sperm",
      "donor-eggs",
      "donor-embryo",
      "known-donor-coordination",
    ];
    return candidates.flatMap((lineId) => {
      const availability = getLineAvailability(
        lineId,
        selectedLocation.estimateLocation,
      );
      return availability ? [{ lineId, availability }] : [];
    });
  }, [selectedLocation.estimateLocation]);
  const availableInRegion = regionAvailability.filter(
    ({ availability }) =>
      availability.status === "funded" ||
      availability.status === "partially-funded",
  );
  const notAvailableInRegion = regionAvailability.filter(
    ({ availability }) =>
      availability.status === "not-available" ||
      availability.status === "restricted",
  );

  const defaultLineKeys = useMemo(
    () =>
      estimateSections.flatMap((section) =>
        section.lines
          .filter((line) => line.defaultSelected)
          .map((line) => line.key),
      ),
    [estimateSections],
  );
  const defaultLineKeySignature = defaultLineKeys.join("|");
  // In read-only (public-system) markets, every line is "selected" so the
  // breakdown totals and the ✓ icons reflect the full coverage picture
  // instead of a user-toggled subset.
  const allLineKeys = useMemo(
    () =>
      estimateSections.flatMap((section) =>
        section.lines.map((line) => line.key),
      ),
    [estimateSections],
  );
  const activeLineKeys = isReadOnlyRegion
    ? allLineKeys
    : selectedLineKeys.length > 0
      ? selectedLineKeys
      : defaultLineKeys;
  const selectedLineKeySet = useMemo(
    () => new Set(activeLineKeys),
    [activeLineKeys],
  );

  useEffect(() => {
    setSelectedLineKeys(defaultLineKeys);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTreatment, diagnosticProfile, defaultLineKeySignature]);

  useEffect(() => {
    if (!hasTransferPath(selectedTreatment)) {
      setTransferCount(1);
    }
  }, [selectedTreatment]);

  const adjustedBreakdown = estimateSections.flatMap((section) =>
    section.lines
      .filter((line) => selectedLineKeySet.has(line.key))
      .map((line) => ({
        key: line.key,
        label: line.label,
        amount: getLineAmount(line, {
          cycleCount,
          embryoCount,
          storageYears,
          transferCount,
        }),
        detail: getStatusLabel(line.status, t),
      })),
  );
  const adjustedTotal = adjustedBreakdown.reduce(
    (sum, item) => sum + item.amount,
    0,
  );
  // Her share is known only once coverage is verified. Until then the figures
  // are the total before insurance, which is also what a self-pay patient
  // pays. They used to be a fixed 42% (insured, unverified) or 34% (no
  // insurance) of the total, shown as an out-of-pocket estimate.
  const patientEstimate =
    hasInsurance && coverageVerified
      ? estimatePatientResponsibility(adjustedTotal, fertilityCoverage)
      : null;
  const expectedPatientResponsibility = patientEstimate ?? adjustedTotal;
  const shareBeforeInsurance = hasInsurance && patientEstimate == null;
  const treatmentLabel = t(TREATMENT_LABEL_KEYS[selectedTreatment]);
  const activeInsuranceLabel = hasInsurance ? insuranceLabel : "Self-pay";
  const scenarioNarrative = buildScenarioNarrative({
    treatment: selectedTreatment,
    location: selectedLocation,
    locationIsSpecific: isLocationSpecificEstimate(
      selectedLocation.estimateLocation,
    ),
    sections: estimateSections,
    selectedLineKeys: activeLineKeys,
    cycleCount,
    medicationIntensity,
    embryoCount,
    storageYears,
    transferCount,
    t,
  });
  const cardNarrative = state === "estimate" ? scenarioNarrative : null;

  const isEstimateView = state === "estimate";
  // Read, never depended on: switching treatment mid-estimate is a step
  // inside the run, not the start of a new one.
  const treatmentRef = useRef(selectedTreatment);
  treatmentRef.current = selectedTreatment;
  useEffect(() => {
    if (!isEstimateView) return;
    estimateFlow.started({ treatment: treatmentRef.current });
    return () => {
      if (estimateLocationChosen.current) return;
      estimateFlow.abandoned("left_without_choosing_location", {
        treatment: treatmentRef.current,
      });
    };
  }, [estimateFlow, isEstimateView]);
  // Never silently swap in another location's estimate: name both the place the
  // person asked for and the estimate we could actually use, right under the
  // number. The message depends on how the selection resolved:
  //   - nearest metro (custom option with a sub-country estimate location)
  //   - national estimate (custom option priced at the country level)
  //   - unsupported country (kept the prior selection, holds a transient hint)
  // For read-only (public-system) regions the breakdown is a coverage list,
  // not a city-level pricing estimate, so the "showing national estimate"
  // nag reads as redundant noise. Keep the location picker hint if the user
  // just searched an unsupported country.
  const locationNoticeMessage = isReadOnlyRegion
    ? locationSearchHint
    : (locationSearchHint ?? deriveLocationNotice(selectedLocation, t));

  const parseQuoteAmount = (value: string | null | undefined): number => {
    const n = Number(String(value ?? "").replace(/[^0-9.]/g, ""));
    return Number.isFinite(n) ? n : 0;
  };
  const devClinicQuoteFixtures = getDevClinicQuoteFixtures();
  const savedClinicQuotes = [
    ...devClinicQuoteFixtures,
    ...(clinicQuotesQuery.data ?? []),
  ];
  const uploadedQuoteCards: ClinicQuote[] = savedClinicQuotes.map(
    (quote, index) => {
      const extraction = quoteExtractions[quote.id];
      const data = quote.extraction ?? extraction?.data;
      const status = quote.extraction
        ? "done"
        : (extraction?.status ?? (quote.uploadId ? "extracting" : "error"));
      const document = { id: quote.id, name: quote.name };
      const fallbackName =
        index === 0
          ? t("costs.money_card.quotes.uploaded_clinic_quote")
          : t("costs.money_card.quotes.uploaded_quote_number", {
              params: { number: String(index + 1) },
            });
      const clinicName = data?.clinicName?.trim() || fallbackName;
      const uploadedAt = t("costs.money_card.quotes.uploaded_quote");
      const logoText = t("costs.money_card.quotes.uploaded_quote_initials");

      const extractedLines = (data?.lineItems ?? []).filter(
        (line) =>
          (line.label && line.label.trim().length > 0) ||
          parseQuoteAmount(line.amount) > 0,
      );
      const extractedTotal = data?.totalAmount
        ? parseQuoteAmount(data.totalAmount)
        : extractedLines.reduce(
            (sum, line) => sum + parseQuoteAmount(line.amount),
            0,
          );

      if (status === "extracting") {
        return {
          id: document.id,
          clinic: clinicName,
          location: selectedLocation.label,
          logoText,
          status: t("costs.money_card.quotes.analyzing"),
          statusTone: "pending",
          quoteTotal: 0,
          outOfPocket: 0,
          uploadedAt,
          documentLabel: document.name,
          quoteDate: data?.quoteDate ?? null,
          summary: t("costs.money_card.quotes.reading_document_for_line_items"),
          analysisStatus: "ready",
          analysisTitle: t("costs.money_card.quotes.analyzing_the_quote"),
          analysisBody: t("costs.money_card.quotes.louise_is_reading_document"),
          missingItems: [],
          lineItems: [],
        };
      }

      if (status === "error" || extractedLines.length === 0) {
        return {
          id: document.id,
          clinic: clinicName,
          location: selectedLocation.label,
          logoText,
          status: t("costs.money_card.quotes.couldnt_read"),
          statusTone: "attention",
          quoteTotal: extractedTotal,
          outOfPocket: 0,
          uploadedAt,
          documentLabel: document.name,
          quoteDate: data?.quoteDate ?? null,
          summary:
            extraction?.error ??
            t("costs.money_card.quotes.no_itemized_charges_read"),
          analysisStatus: "needs-upload",
          analysisTitle: t("costs.money_card.quotes.no_line_items_found"),
          analysisBody: t(
            "costs.money_card.quotes.couldnt_read_itemized_charges",
          ),
          missingItems: [],
          lineItems: [],
        };
      }

      // Extraction succeeded with real line items.
      const extractedClinicName = data?.clinicName?.trim();
      const lineCountForm = extractedLines.length === 1 ? "one" : "other";
      return {
        id: document.id,
        clinic: clinicName,
        location: selectedLocation.label,
        logoText,
        status: t("costs.money_card.quotes.analyzed"),
        statusTone: "ready",
        quoteTotal: extractedTotal,
        // This quote's own total, through her plan once it is verified.
        outOfPocket:
          (hasInsurance && coverageVerified
            ? estimatePatientResponsibility(extractedTotal, fertilityCoverage)
            : null) ?? extractedTotal,
        uploadedAt,
        documentLabel: document.name,
        quoteDate: data?.quoteDate ?? null,
        summary: extractedClinicName
          ? t(
              `costs.money_card.quotes.read_line_items_from_clinic_${lineCountForm}`,
              {
                params: {
                  count: String(extractedLines.length),
                  clinic: extractedClinicName,
                },
              },
            )
          : t(`costs.money_card.quotes.read_line_items_${lineCountForm}`, {
              params: { count: String(extractedLines.length) },
            }),
        analysisStatus: "ready",
        analysisTitle: t("costs.money_card.quotes.quote_analyzed"),
        analysisBody: t(
          "costs.money_card.quotes.line_items_read_from_document",
        ),
        missingItems: [],
        lineItems: extractedLines.map((line, lineIndex) => ({
          key: `${document.id}-line-${lineIndex}`,
          label:
            line.label?.trim() ||
            t("costs.money_card.quotes.line_number", {
              params: { number: String(lineIndex + 1) },
            }),
          amount: parseQuoteAmount(line.amount),
          category: line.category?.trim() || null,
          status: t("costs.money_card.quotes.from_quote"),
          coverage: t("costs.money_card.quotes.check_coverage"),
          financing: "—",
          detail: line.notes?.trim()
            ? line.notes.trim()
            : line.category?.trim()
              ? t("costs.money_card.quotes.in_category", {
                  params: { category: line.category.trim() },
                })
              : t("costs.money_card.quotes.read_from_uploaded_quote"),
        })),
      };
    },
  );
  const quoteCards = uploadedQuoteCards;
  const comparableQuoteCards = quoteCards.filter(
    (quote) =>
      quote.statusTone === "ready" &&
      Number.isFinite(quote.quoteTotal) &&
      quote.quoteTotal >= 0,
  );
  const uploadedQuoteCount = comparableQuoteCards.length;
  const quoteSummaryAmount =
    uploadedQuoteCount > 0
      ? Math.min(...comparableQuoteCards.map((quote) => quote.quoteTotal))
      : null;

  const savedPaymentRecords = paymentRecordsQuery.data ?? [];
  const devPaymentRecordFixtures = getDevPaymentRecordFixtures();
  const summaryRecords =
    devPaymentRecordFixtures ??
    savedPaymentRecords.map((record) => apiToUiPaymentRecord(record, locale));
  const {
    paid: paidToDate,
    claims: submittedClaimAmount,
    leftToPay,
  } = summarizePaymentRecords(summaryRecords, expectedPatientResponsibility);
  // Only domain data is stored; the mapper derives all display copy at render
  // time (locale-aware headlines, date labels, split-line colors, ...).
  const basePaymentRecords: PaymentRecord[] = savedPaymentRecords.map(
    (record) => apiToUiPaymentRecord(record, locale),
  );
  const mappedPaymentRecords = basePaymentRecords.map((record) => {
    const attachments = paymentDocumentAttachments[record.id] ?? [];
    const withAttachments =
      attachments.length > 0
        ? { ...record, documents: [...attachments, ...record.documents] }
        : record;
    const editedRecord = editedPaymentRecords[record.id];

    return editedRecord
      ? {
          ...withAttachments,
          ...editedRecord,
          documents: withAttachments.documents,
        }
      : withAttachments;
  });
  const paymentRecords = (
    devPaymentRecordFixtures ?? mappedPaymentRecords
  ).filter((record) => !deletedPaymentRecordIds.includes(record.id));
  const selectedPaymentRecord =
    paymentRecords.find((record) => record.id === selectedPaymentRecordId) ??
    null;
  const paymentBreakdownSegments: PaymentBreakdownSegment[] = [
    {
      label: t("costs.money_card.payments.paid"),
      value: paidToDate,
      detail: t("costs.money_card.payments.recorded"),
      color: ORGANIC_BAR_COLORS.paid,
    },
    {
      label: t("costs.money_card.payments.claims"),
      value: submittedClaimAmount,
      detail: coverageVerified
        ? t("costs.money_card.payments.in_review")
        : t("costs.money_card.payments.needs_plan"),
      color: ORGANIC_BAR_COLORS.claims,
    },
    {
      label: t("costs.money_card.payments.left"),
      value: leftToPay,
      detail: t("costs.money_card.payments.expected"),
      color: ORGANIC_BAR_COLORS.left,
    },
  ];
  const paymentPreviewRecords = paymentRecords.slice(
    0,
    PAYMENT_RECORD_PREVIEW_LIMIT,
  );
  const openFullPaymentsList = () =>
    onViewAllPayments({
      records: paymentRecords,
      segments: paymentBreakdownSegments,
      leftToPay,
      patientResponsibility: expectedPatientResponsibility,
    });

  const summary = getCostStateSummary({
    state,
    treatmentLabel,
    estimateTotal: formatCurrency(adjustedTotal, locale, {
      currency: estimateCurrency,
    }),
    patientEstimate:
      patientEstimate != null
        ? formatCurrency(patientEstimate, locale, {
            currency: estimateCurrency,
          })
        : null,
    quoteTotal:
      quoteSummaryAmount != null
        ? formatCurrency(quoteSummaryAmount, locale, {
            currency: estimateCurrency,
          })
        : "No quote yet",
    quoteCount: quoteCards.length,
    comparableQuoteCount: uploadedQuoteCount,
    paymentDue: formatCurrency(leftToPay, locale, {
      currency: estimateCurrency,
    }),
    isPublicCoverageRegion: isReadOnlyRegion,
    hasInsurance,
    coverageVerified,
    insuranceLabel: activeInsuranceLabel,
    clinicCount,
    verifiedClinicCount,
    t,
  });
  const summaryAmountValue =
    state === "clinic-estimate"
      ? (quoteSummaryAmount ?? 0)
      : state === "payments"
        ? leftToPay
        : (patientEstimate ?? adjustedTotal);
  const showHeroSummary =
    state !== "payments" &&
    !(state === "clinic-estimate" && quoteSummaryAmount == null);

  const runPrimaryAction = () => {
    if (state === "clinic-estimate" && quoteCards.length > 0) {
      onCompareQuotes({
        quotes: quoteCards,
        treatmentLabel,
        careLocation: selectedLocation.label,
        hasInsurance,
        verificationStatus,
        primaryPolicy,
        fertilityCoverage,
      });
      return;
    }
    if (summary.primaryRoute) {
      onNavigate(summary.primaryRoute);
      return;
    }
    onAddInsurance();
  };
  const PrimaryActionIcon =
    state === "clinic-estimate" && quoteCards.length > 0
      ? MessageCircle
      : Shield;
  const SecondaryActionIcon =
    summary.secondaryRoute === "Financing"
      ? CreditCard
      : summary.secondaryRoute === "TaxDeductions"
        ? Receipt
        : ChevronRight;

  const treatmentOptions = CONCEPT_TREATMENT_OPTIONS.map((treatment) => ({
    value: treatment,
    label: t(TREATMENT_LABEL_KEYS[treatment]),
  }));
  const locationSelectOptions = locationOptions.map((location) => ({
    value: location.id,
    label: location.label,
    detail: location.detail,
    disabled:
      location.action === "request-current-location" &&
      locationRequestStatus === "loading",
  }));
  // Bias the Places autocomplete toward where the person is so, for example,
  // typing "San" surfaces nearby cities first. Prefer their detected/precise
  // coordinates, then a coarse centroid from their profile state or ZIP; when
  // we have neither we omit the bias and let Google fall back to IP biasing.
  const locationBiasCenter = useMemo(() => {
    const detected = detectedLocation?.estimateLocation;
    if (detected?.latitude != null && detected?.longitude != null) {
      return { latitude: detected.latitude, longitude: detected.longitude };
    }
    const stateCode =
      normalizeUsStateCode(profileState) ??
      inferUsStateCodeFromZip(profileZipCode);
    return getUsStateCentroid(stateCode);
  }, [detectedLocation, profileState, profileZipCode]);
  // PillSelect already debounces its query, so search immediately here. The
  // control is only open when `openAssumptionControl === "location"`.
  const { predictions: placePredictions, isSearching: isSearchingPlaces } =
    usePlacesPredictions({
      query: locationQuery,
      minChars: 1,
      debounceMs: 0,
      regionsOnly: true,
      biasLocation: locationBiasCenter,
      enabled: openAssumptionControl === "location",
    });
  // While the person is searching, the dropdown shows live Places predictions
  // (prefixed so `handleSelectLocation` can tell them from the saved options);
  // otherwise it shows the suggested + saved locations.
  const placePredictionOptions = placePredictions.map((prediction) => ({
    value: `place:${prediction.placeId}`,
    label: prediction.structuredFormatting.mainText,
    detail: prediction.structuredFormatting.secondaryText || undefined,
  }));
  const locationDropdownOptions =
    locationQuery.trim().length > 0
      ? placePredictionOptions
      : locationSelectOptions;
  const handleLocationChange = async (nextLocationId: string) => {
    const nextLocation = locationOptions.find(
      (location) => location.id === nextLocationId,
    );

    if (nextLocation?.action !== "request-current-location") {
      setSelectedLocationId(nextLocationId);
      setLocationSearchHint(null);
      if (nextLocation) {
        captureLocationIntent({
          selectionSource: "suggested",
          requestedLocation: nextLocation.label,
          normalizedLocation: getEstimateAreaAnalyticsLabel(nextLocation),
          estimateLocation: getEstimateAreaAnalyticsLabel(nextLocation),
          matchType: nextLocation.estimateAreaLabel
            ? "nearest_fallback"
            : "exact",
        });
      }
      return;
    }

    setLocationRequestStatus("loading");
    try {
      const currentLocation = await requestCurrentEstimateAreaOption();
      setDetectedLocation(currentLocation);
      setSelectedLocationId(currentLocation.id);
      setLocationRequestStatus("idle");
      setLocationSearchHint(null);
      captureLocationIntent({
        selectionSource: "current_location",
        requestedLocation: currentLocation.label,
        normalizedLocation: getEstimateAreaAnalyticsLabel(currentLocation),
        estimateLocation: getEstimateAreaAnalyticsLabel(currentLocation),
        matchType: "exact",
      });
    } catch {
      setLocationRequestStatus("error");
    }
  };
  const applyPlaceResolution = (resolution: PlaceEstimateResolution) => {
    if (resolution.status === "unsupported") {
      // Keep the previous selection; a wrong-country estimate would be worse
      // than none. Surface why, and record the intent.
      setLocationSearchHint(resolution.hint);
    } else if (resolution.status === "exact") {
      setSelectedLocationId(resolution.option.id);
      setLocationSearchHint(null);
    } else {
      // nearest metro or national-country estimate: keep the entered location
      // in the list and select it.
      setCustomLocation(resolution.option);
      setSelectedLocationId(resolution.option.id);
      setLocationSearchHint(null);
    }

    const matchType =
      resolution.status === "exact"
        ? "exact"
        : resolution.status === "nearest"
          ? "nearest_fallback"
          : resolution.status === "country"
            ? "country_estimate"
            : "unsupported";
    captureLocationIntent({
      selectionSource: "search",
      requestedLocation: resolution.requestedLabel,
      normalizedLocation:
        resolution.status === "unsupported" ? null : resolution.normalizedLabel,
      estimateLocation:
        resolution.status === "unsupported"
          ? null
          : getEstimateAreaAnalyticsLabel(resolution.option),
      matchType,
    });
  };
  const handleLocationSearch = (query: string) => {
    setLocationQuery(query);
  };
  const handleSelectLocation = async (value: string) => {
    if (!value.startsWith("place:")) {
      await handleLocationChange(value);
      return;
    }
    const placeId = value.slice("place:".length);
    const prediction = placePredictions.find(
      (item) => item.placeId === placeId,
    );
    const details = await getPlaceDetails(placeId);
    const area = details ? extractPlaceArea(details) : null;
    if (!area) {
      setLocationSearchHint(
        t("costs.money_card.location.couldnt_read_location"),
      );
      return;
    }
    applyPlaceResolution(
      resolvePlaceEstimateArea({
        granularity: area.granularity,
        label: prediction?.structuredFormatting.mainText ?? area.label,
        city: area.city,
        stateCode: area.stateCode,
        countryCode: area.countryCode,
        countryName: area.countryName,
        latitude: area.latitude,
        longitude: area.longitude,
      }),
    );
  };
  const { mutateAsync: persistQuoteExtraction } = updateClinicQuoteMutation;
  const runQuoteExtraction = useCallback(
    async (quoteId: string, uploadId: string | null | undefined) => {
      if (!uploadId) {
        setQuoteExtractions((prev) => ({
          ...prev,
          [quoteId]: {
            status: "error",
            error: t("costs.money_card.quotes.upload_did_not_complete"),
          },
        }));
        return;
      }
      setQuoteExtractions((prev) => ({
        ...prev,
        [quoteId]: { status: "extracting" },
      }));

      // Retry transient processing failures before surfacing "couldn't read".
      const maxAttempts = 3;
      let lastError: unknown;
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
          const outcome = await transfer.processSource(
            {
              file: {
                uri: "",
                name: `clinic-quote-${quoteId}`,
                mimeType: "application/pdf",
              },
              uploadId,
            },
            {
              attempts: 24,
              intervalMs: 1_250,
            },
          );
          const quote = projectDocumentProcessingRunToPrefill(
            outcome.run,
            `clinic-quote-${quoteId}`,
          ).quote;
          const data: QuoteOCRResult = {
            clinicName: quote.clinicName || null,
            quoteDate: quote.quoteDate || null,
            currency: quote.currency || null,
            totalAmount: quote.totalAmount || null,
            lineItems: quote.lineItems.map((line) => ({
              label: line.label || null,
              amount: line.amount || null,
              category: line.category || null,
              notes: line.notes || null,
            })),
          };
          // When the call succeeds but yields nothing usable, log shape only (no
          // document content) so we can tell "backend returned no line items"
          // apart from a network/auth failure when triaging "couldn't read".
          const lineItemCount = Array.isArray(data?.lineItems)
            ? data.lineItems.length
            : null;
          if (!lineItemCount) {
            console.warn("[QuoteOCR] extraction returned no line items", {
              hasData: !!data,
              lineItemCount,
              hasTotal: !!data?.totalAmount,
            });
          }
          // The OCR result can omit fields at runtime; normalize to the
          // session shape before saving it on the clinic-quote record.
          const extraction: ClinicQuoteExtraction = {
            clinicName: data?.clinicName ?? null,
            quoteDate: data?.quoteDate ?? null,
            currency: data?.currency ?? null,
            totalAmount: data?.totalAmount ?? null,
            lineItems: (data?.lineItems ?? []).map((line) => ({
              label: line?.label ?? null,
              amount: line?.amount ?? null,
              category: line?.category ?? null,
              notes: line?.notes ?? null,
            })),
          };
          await persistQuoteExtraction({
            clinicQuoteId: quoteId,
            updates: { extraction },
          });
          setQuoteExtractions((prev) => ({
            ...prev,
            [quoteId]: { status: "done", data },
          }));
          return;
        } catch (err) {
          lastError = err;
          if (attempt < maxAttempts) {
            await new Promise((resolve) => setTimeout(resolve, 400 * attempt));
          }
        }
      }
      console.warn("[QuoteOCR] extraction failed", lastError);
      setQuoteExtractions((prev) => ({
        ...prev,
        [quoteId]: {
          status: "error",
          error:
            lastError instanceof Error
              ? lastError.message
              : t("costs.money_card.quotes.analysis_failed"),
        },
      }));
    },
    [persistQuoteExtraction, t, transfer],
  );

  // Session quotes that have not received an extraction are analyzed once;
  // the ref keeps re-renders from re-triggering the same quote.
  useEffect(() => {
    for (const quote of clinicQuotesQuery.data ?? []) {
      if (quote.extraction || !quote.uploadId) continue;
      if (attemptedQuoteExtractionsRef.current.has(quote.id)) continue;
      attemptedQuoteExtractionsRef.current.add(quote.id);
      void runQuoteExtraction(quote.id, quote.uploadId);
    }
  }, [clinicQuotesQuery.data, runQuoteExtraction]);

  const retryQuoteExtraction = (quoteId: string) => {
    const quote = savedClinicQuotes.find(
      (candidate) => candidate.id === quoteId,
    );
    if (!quote) return;
    attemptedQuoteExtractionsRef.current.delete(quoteId);
    void runQuoteExtraction(quoteId, quote.uploadId);
  };

  const quoteDeleteTarget = quoteDeleteTargetId
    ? (savedClinicQuotes.find((quote) => quote.id === quoteDeleteTargetId) ??
      null)
    : null;
  const deleteQuote = async () => {
    if (!quoteDeleteTargetId) return;
    setQuoteDeleteError(null);
    try {
      await deleteClinicQuoteMutation.mutateAsync(quoteDeleteTargetId);
      setExpandedQuoteIds((ids) =>
        ids.filter((id) => id !== quoteDeleteTargetId),
      );
      attemptedQuoteExtractionsRef.current.delete(quoteDeleteTargetId);
      setQuoteExtractions((current) => {
        const next = { ...current };
        delete next[quoteDeleteTargetId];
        return next;
      });
      setQuoteDeleteTargetId(null);
    } catch (error) {
      setQuoteDeleteError(
        error instanceof Error
          ? error.message
          : t("costs.money_card.quotes.couldnt_delete_quote"),
      );
    }
  };

  // Only domain data enters the session cache — display copy is derived at
  // render time in apiToUiPaymentRecord. The status is workflow state, so it's
  // set here once.
  const buildManualPaymentRecordPayload = (
    entry: ManualPaymentEntry,
  ): CreatePaymentRecordPayload => {
    const status: PaymentRecordStatus =
      entry.kind === "payment"
        ? "paid"
        : entry.kind === "claim"
          ? coverageVerified
            ? "waiting-eob"
            : "submitted"
          : entry.kind === "receipt"
            ? "needs-receipt"
            : "due";

    if (entry.attachmentName && !entry.attachmentDocumentId) {
      throw new Error("Attached payment document is missing its document ID");
    }

    const amount = toDecimalAmount(entry.amount);
    return {
      kind: entry.kind,
      entrySource: "manual",
      label: entry.label,
      provider: entry.provider,
      amount,
      billedAmount: amount,
      status,
      occurredOn: parseDateText(entry.date) ?? todayIsoDate(),
      note: entry.note || undefined,
      documents: entry.attachmentName
        ? [
            {
              id: entry.attachmentDocumentId as string,
              label: entry.attachmentName,
              status: "attached" as const,
              uploadId: entry.attachmentUploadId ?? null,
            },
          ]
        : [],
      // Synthesize a single line item so a manually added payment or
      // receipt feeds useTaxDeductionCalculation the same way an uploaded
      // receipt does. Mirrors buildManualPaymentRecordPayload in
      // taxReviewHelpers.ts — kept in sync by hand. Claims and balance
      // records aren't out-of-pocket medical expenses, so they stay off
      // the tax calculator by design.
      lineItems:
        entry.kind === "payment" || entry.kind === "receipt"
          ? [
              {
                id: `${entry.id}-line`,
                label: entry.category || entry.label || "Manual entry",
                amount,
                detail: "",
                allocations: [
                  {
                    id: `${entry.id}-patient`,
                    kind: "patient" as const,
                    label: "Paid by you",
                    amount,
                  },
                ],
              },
            ]
          : [],
    };
  };

  const buildUploadedPaymentRecordPayload = (
    document: UploadedCostDocument,
  ): CreatePaymentRecordPayload => {
    if (!document.documentId || !document.uploadId) {
      throw new Error("Uploaded document is missing canonical linkage");
    }
    const kind = document.paymentKind ?? "payment";
    const status: PaymentRecordStatus =
      kind === "payment"
        ? "paid"
        : kind === "claim"
          ? coverageVerified
            ? "waiting-eob"
            : "submitted"
          : kind === "receipt"
            ? "needs-receipt"
            : "due";

    const uploadedDocumentLabelKeys: Record<ManualPaymentKind, string> = {
      payment: "costs.cost_upload.uploaded_documents.payment_document",
      claim: "costs.cost_upload.uploaded_documents.claim_document",
      receipt: "costs.cost_upload.uploaded_documents.receipt_document",
      balance: "costs.cost_upload.uploaded_documents.balance_document",
    };

    return {
      kind,
      entrySource: "document_upload",
      label: t(uploadedDocumentLabelKeys[kind]),
      provider:
        document.source === "camera"
          ? t("costs.cost_upload.uploaded_documents.uploaded_photo")
          : t("costs.cost_upload.uploaded_documents.uploaded_file"),
      amount: "0",
      billedAmount: "0",
      status,
      occurredOn: todayIsoDate(),
      documents: [
        toAttachedCostDocumentReference({
          documentId: document.documentId,
          uploadId: document.uploadId,
          name: document.name,
        }),
      ],
    };
  };

  const handleCostDocumentPicked = async (document: UploadedCostDocument) => {
    if (document.mode === "quote") {
      if (!document.uploadId) {
        throw new Error("Uploaded quote is missing its upload ID");
      }
      try {
        // A completed create can be committed server-side even if the client
        // loses the response. Refresh first so retrying the same upload does
        // not create a second quote.
        const refreshedQuotes = await clinicQuotesQuery.refetch();
        const existing = refreshedQuotes.data?.find(
          (quote) => quote.uploadId === document.uploadId,
        );
        const created =
          existing ??
          (await createClinicQuoteMutation.mutateAsync({
            name: document.name,
            uploadId: document.uploadId,
            mimeType: document.mimeType,
          }));
        setExpandedQuoteIds((ids) => Array.from(new Set([created.id, ...ids])));
        if (!created.extraction) {
          attemptedQuoteExtractionsRef.current.add(created.id);
          void runQuoteExtraction(created.id, created.uploadId);
        }
      } catch (err) {
        console.error("[Costs] failed to save clinic quote", err);
        throw err;
      }
      return;
    }

    if (!document.uploadId) {
      throw new Error("Uploaded payment is missing its upload ID");
    }
    const uploadId = document.uploadId;
    const refreshedRecords = await paymentRecordsQuery.refetch();
    const existing = refreshedRecords.data?.find((record) =>
      hasCostDocumentUpload(record.documents ?? [], uploadId),
    );
    const created =
      existing ??
      (await createPaymentRecordMutation.mutateAsync(
        buildUploadedPaymentRecordPayload(document),
      ));
    setSelectedPaymentRecordId(created.id);
  };
  const handleManualPaymentAdded = async (entry: ManualPaymentEntry) => {
    const refreshedRecords = entry.attachmentUploadId
      ? await paymentRecordsQuery.refetch()
      : null;
    const existing = entry.attachmentUploadId
      ? refreshedRecords?.data?.find((record) =>
          hasCostDocumentUpload(
            record.documents ?? [],
            entry.attachmentUploadId as string,
          ),
        )
      : undefined;
    const created =
      existing ??
      (await createPaymentRecordMutation.mutateAsync(
        buildManualPaymentRecordPayload(entry),
      ));
    setSelectedPaymentRecordId(created.id);
  };
  const handlePaymentRecordSaved = async (record: CostPaymentRecordParam) => {
    const updated = await updatePaymentRecordMutation.mutateAsync({
      paymentRecordId: record.id,
      updates: uiToPaymentRecordUpdates(record),
    });
    const mapped = apiToUiPaymentRecord(updated, locale);
    setEditedPaymentRecords((records) => ({
      ...records,
      [record.id]: mapped as PaymentRecord,
    }));
    setSelectedPaymentRecordId(record.id);
  };
  const attachPaymentDocument = async (document: UploadedCostDocument) => {
    if (!attachmentRecordId) throw new Error("No payment record selected");
    const record = paymentRecords.find(
      (candidate) => candidate.id === attachmentRecordId,
    );
    if (!record) throw new Error("Payment record is no longer available");
    if (!document.documentId || !document.uploadId) {
      throw new Error("Uploaded document is missing canonical linkage");
    }

    const attached = toAttachedCostDocumentReference({
      documentId: document.documentId,
      uploadId: document.uploadId,
      name: document.name,
    });
    const documents = mergeCostDocumentReferences(record.documents, attached);
    const savedRecord = savedPaymentRecords.find(
      (candidate) => candidate.id === record.id,
    );
    if (savedRecord) {
      await updatePaymentRecordMutation.mutateAsync({
        paymentRecordId: record.id,
        updates: { documents },
      });
    } else {
      setPaymentDocumentAttachments((attachments) => ({
        ...attachments,
        [record.id]: [
          {
            id: attached.id,
            label: document.name,
            detail: t("costs.money_card.payments.attached_to_this_ledger_item"),
            status: "attached" as const,
            uploadId: attached.uploadId,
          },
          ...(attachments[record.id] ?? []),
        ],
      }));
    }
    setAttachmentRecordId(null);
  };
  const deletePaymentRecord = async (recordId: string) => {
    await deletePaymentRecordMutation.mutateAsync(recordId);
    setDeletedPaymentRecordIds((ids) =>
      Array.from(new Set([...ids, recordId])),
    );
    setSelectedPaymentRecordId(null);
  };
  const toggleLine = (key: string) => {
    setSelectedLineKeys((keys) =>
      keys.includes(key)
        ? keys.filter((lineKey) => lineKey !== key)
        : [...keys, key],
    );
  };
  const toggleSection = (section: EstimateScenarioSection) => {
    const sectionKeys = section.lines.map((line) => line.key);
    const allSelected = sectionKeys.every((key) => selectedLineKeySet.has(key));

    setSelectedLineKeys((keys) => {
      if (allSelected) {
        return keys.filter((key) => !sectionKeys.includes(key));
      }

      return Array.from(new Set([...keys, ...sectionKeys]));
    });
  };
  const setAssumptionControlOpen = (
    control: "treatment" | "location",
    open: boolean,
  ) => {
    if (open) {
      estimateFlow.stepViewed(control, { treatment: selectedTreatment });
      setOpenAssumptionControl(control);
      setShowBreakdown(false);
      return;
    }
    setOpenAssumptionControl((current) =>
      current === control ? null : current,
    );
  };
  const toggleScenarioBuilder = () => {
    if (!showBreakdown) {
      estimateFlow.stepViewed("breakdown", { treatment: selectedTreatment });
    }
    setOpenAssumptionControl(null);
    setShowBreakdown((value) => !value);
  };
  const renderAssumptionDivider = (visible: boolean) =>
    visible ? (
      <View
        style={{
          width: 1,
          height: 22,
          backgroundColor: semanticColors.border.subtle,
        }}
      />
    ) : null;

  const renderAssumptions = ({
    compact = false,
  }: { compact?: boolean } = {}) => {
    const usesControlBar = compact && topControlsVariant === "bar";
    const scenarioToggleLabel = showBreakdown
      ? t("costs.money_card.scenario.hide_scenario")
      : t("costs.money_card.scenario.scenario_builder");

    return (
      <View
        style={{
          flexDirection: "row",
          flexWrap: compact ? "nowrap" : "wrap",
          justifyContent: compact ? "center" : "flex-end",
          gap: usesControlBar ? 0 : compact ? 6 : 8,
          alignItems: compact ? "center" : "flex-start",
          zIndex: 20,
          width: compact ? "100%" : undefined,
          ...(usesControlBar
            ? {
                minHeight: 38,
                borderRadius: 999,
                borderWidth: 1,
                borderColor: semanticColors.border.subtle,
                backgroundColor: semanticColors.surface.muted,
                padding: 3,
              }
            : null),
        }}
      >
        <View
          style={
            usesControlBar
              ? {
                  flexGrow: 1,
                  flexShrink: 1,
                  flexBasis: 0,
                  minWidth: 0,
                }
              : compact
                ? {
                    flexGrow: 1,
                    flexShrink: 1,
                    flexBasis: 0,
                    maxWidth: 205,
                    minWidth: 0,
                  }
                : undefined
          }
        >
          <PillSelect
            value={selectedTreatment}
            options={treatmentOptions}
            onChange={(next) => {
              estimateFlow.stepSubmitted("treatment", { treatment: next });
              setSelectedTreatment(next as TreatmentType);
            }}
            icon={ClipboardList}
            accessibilityLabel={t("costs.money_card.scenario.treatment")}
            compact={compact}
            surface={usesControlBar ? "bar" : "pill"}
            open={openAssumptionControl === "treatment"}
            onOpenChange={(open) => setAssumptionControlOpen("treatment", open)}
            style={compact ? { width: "100%" } : { maxWidth: 210 }}
          />
        </View>
        {renderAssumptionDivider(usesControlBar)}
        <View
          style={
            usesControlBar
              ? {
                  flexGrow: 0,
                  flexShrink: 0,
                  flexBasis: 92,
                  width: 92,
                }
              : compact
                ? {
                    flexGrow: 0,
                    flexShrink: 0,
                    flexBasis: 92,
                    width: 92,
                  }
                : undefined
          }
        >
          <PillSelect
            value={selectedLocation.id}
            options={locationDropdownOptions}
            onChange={handleSelectLocation}
            icon={MapPin}
            accessibilityLabel={t("costs.money_card.scenario.location")}
            placeholder={selectedLocation.label}
            searchable
            searchPlaceholder={t(
              "costs.money_card.location.city_state_or_country",
            )}
            onSearch={handleLocationSearch}
            searching={isSearchingPlaces}
            filterOptionsLocally={false}
            compact={compact}
            surface={usesControlBar ? "bar" : "pill"}
            open={openAssumptionControl === "location"}
            onOpenChange={(open) => {
              setAssumptionControlOpen("location", open);
              // Clearing the query resets predictions via the hook (which also
              // idles once the control is no longer the open assumption).
              if (!open) setLocationQuery("");
            }}
            style={compact ? { width: "100%" } : { maxWidth: 210 }}
          />
        </View>
        {renderAssumptionDivider(usesControlBar)}
        <Pressable
          onPress={() => {
            capturePress(
              pressLabel(scenarioToggleLabel),
              showBreakdown ? "hide_scenario" : "open_scenario_builder",
            );
            toggleScenarioBuilder();
          }}
          accessibilityRole="button"
          accessibilityLabel={scenarioToggleLabel}
          style={{
            minHeight: compact ? 32 : 36,
            flexGrow: compact ? 0 : undefined,
            flexShrink: compact ? 0 : undefined,
            flexBasis: compact ? (usesControlBar ? 174 : 178) : undefined,
            width: compact ? (usesControlBar ? 174 : 178) : undefined,
            minWidth: compact ? (usesControlBar ? 174 : 178) : 0,
            maxWidth: compact ? undefined : 210,
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            gap: compact ? 5 : 6,
            paddingLeft: compact ? 10 : 11,
            paddingRight: compact ? 8 : 9,
            paddingVertical: compact ? 6 : 7,
            borderRadius: 999,
            borderWidth: usesControlBar ? 0 : showBreakdown ? 1 : 0,
            borderColor: semanticColors.border.strong,
            backgroundColor: usesControlBar
              ? "transparent"
              : showBreakdown
                ? withAlpha(semanticColors.text.primary, 0.11)
                : compact
                  ? withAlpha(semanticColors.text.primary, 0.065)
                  : withAlpha(semanticColors.text.primary, 0.08),
          }}
        >
          <SlidersHorizontal
            size={compact ? 12 : 13}
            color={
              usesControlBar && showBreakdown
                ? EMBER.colors.text
                : EMBER.colors.textLight
            }
            strokeWidth={usesControlBar && showBreakdown ? 2 : 1.8}
          />
          <Text
            className="font-dm-sans"
            numberOfLines={1}
            style={{
              flexShrink: 1,
              minWidth: 0,
              fontSize: compact ? 12 : 13,
              fontWeight: usesControlBar && showBreakdown ? "800" : "700",
              color: EMBER.colors.text,
            }}
          >
            {scenarioToggleLabel}
          </Text>
          {showBreakdown ? (
            <Minus
              size={compact ? 12 : 13}
              color={
                usesControlBar && showBreakdown
                  ? EMBER.colors.text
                  : EMBER.colors.textLight
              }
              strokeWidth={usesControlBar && showBreakdown ? 2.2 : 2}
              style={{ marginLeft: "auto" }}
            />
          ) : (
            <Plus
              size={compact ? 12 : 13}
              color={EMBER.colors.textLight}
              strokeWidth={2}
              style={{ marginLeft: "auto" }}
            />
          )}
        </Pressable>
      </View>
    );
  };

  const renderScenarioControls = () => (
    <CardSurface variant="panel" style={{ overflow: "hidden" }}>
      <View
        style={{
          flexDirection: "row",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          padding: 14,
          paddingBottom: 12,
        }}
      >
        <View style={{ gap: 2 }}>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 13,
              fontWeight: "700",
              color: EMBER.colors.text,
            }}
          >
            {t("costs.money_card.scenario.scenario_assumptions")}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              maxWidth: 310,
              fontSize: 12,
              lineHeight: 17,
              color: EMBER.colors.textLight,
            }}
          >
            {t("costs.money_card.scenario.quotes_can_replace_assumptions")}
          </Text>
        </View>
        <Text
          className="font-dm-sans"
          style={{ fontSize: 12, color: EMBER.colors.textLight }}
        >
          {t(
            activeLineKeys.length === 1
              ? "costs.money_card.scenario.items_selected_one"
              : "costs.money_card.scenario.items_selected_other",
            { params: { count: String(activeLineKeys.length) } },
          )}
        </Text>
      </View>

      <View style={{ gap: 10, paddingHorizontal: 14, paddingBottom: 14 }}>
        {estimateSections.some((section) =>
          section.lines.some((line) => line.multiplier === "cycle"),
        ) ? (
          <View
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 10,
            }}
          >
            <Text
              className="font-dm-sans"
              style={{ fontSize: 12, color: EMBER.colors.textLight }}
            >
              {t("costs.money_card.scenario.treatment_cycles")}
            </Text>
            <MoonStepper
              value={cycleCount}
              onChange={setCycleCount}
              min={1}
              max={4}
              accentColor={EMBER.colors.textLight}
            />
          </View>
        ) : null}

        {hasMedicationEstimate(selectedTreatment) ? (
          <View style={{ gap: 7 }}>
            <Text
              className="font-dm-sans"
              style={{ fontSize: 12, color: EMBER.colors.textLight }}
            >
              {t("costs.money_card.scenario.medication_intensity")}
            </Text>
            <MoonSegmentedControl
              tabs={(["low", "typical", "high"] as const).map((option) => ({
                value: option,
                label: t(
                  `costs.money_card.scenario.medication_intensity_levels.${option}`,
                ),
              }))}
              active={medicationIntensity}
              onChange={setMedicationIntensity}
              style={{ marginTop: 0 }}
            />
          </View>
        ) : null}

        {estimateSections.some((section) =>
          section.lines.some((line) => line.multiplier === "embryo"),
        ) ? (
          <View
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 10,
            }}
          >
            <Text
              className="font-dm-sans"
              style={{ fontSize: 12, color: EMBER.colors.textLight }}
            >
              {t("costs.money_card.scenario.embryos_for_pgt_a")}
            </Text>
            <MoonStepper
              value={embryoCount}
              onChange={setEmbryoCount}
              min={1}
              max={12}
              accentColor={EMBER.colors.textLight}
            />
          </View>
        ) : null}

        {estimateSections.some((section) =>
          section.lines.some((line) => line.multiplier === "storage-year"),
        ) ? (
          <View
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 10,
            }}
          >
            <Text
              className="font-dm-sans"
              style={{ fontSize: 12, color: EMBER.colors.textLight }}
            >
              {t("costs.money_card.scenario.storage_years")}
            </Text>
            <MoonStepper
              value={storageYears}
              onChange={setStorageYears}
              min={1}
              max={10}
              accentColor={EMBER.colors.textLight}
            />
          </View>
        ) : null}

        {hasTransferPath(selectedTreatment) ? (
          <View
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 10,
            }}
          >
            <Text
              className="font-dm-sans"
              style={{ fontSize: 12, color: EMBER.colors.textLight }}
            >
              {t("costs.money_card.scenario.transfer_cycles")}
            </Text>
            <MoonStepper
              value={transferCount}
              onChange={setTransferCount}
              min={1}
              max={4}
              accentColor={EMBER.colors.textLight}
            />
          </View>
        ) : null}
      </View>

      <View
        style={{
          borderTopWidth: 1,
          borderTopColor: semanticColors.border.subtle,
        }}
      >
        {estimateSections.map(renderEstimateSection)}
      </View>

      <View
        style={{
          borderTopWidth: 1,
          borderTopColor: semanticColors.border.subtle,
          padding: 12,
        }}
      >
        {renderInsuranceEstimateState()}
      </View>
    </CardSurface>
  );

  // Public-system markets (France today) see a curated summary ALWAYS, above
  // the scenario builder's Collapsible — intro + Available/Not available
  // lists. The detailed per-line breakdown stays inside the Collapsible so
  // the default experience is a quick coverage picture, with the option to
  // "Scenario builder" into the full view.
  const renderPublicCoverageSummary = () => {
    if (!isReadOnlyRegion) return null;
    if (availableInRegion.length === 0 && notAvailableInRegion.length === 0) {
      return null;
    }
    return (
      <CardSurface
        variant="panel"
        style={{ marginTop: 12, overflow: "hidden" }}
      >
        {selectedLocation.estimateLocation.countryCode === "FR" ? (
          <View
            style={{
              paddingVertical: 12,
              paddingHorizontal: 14,
            }}
          >
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                lineHeight: 17,
                color: EMBER.colors.textMid,
              }}
            >
              {t("costs.money_card.scenario.fr_coverage_intro")}
            </Text>
          </View>
        ) : null}

        {availableInRegion.length > 0 ? (
          <View
            style={{
              borderTopWidth: 1,
              borderTopColor: semanticColors.border.subtle,
              paddingVertical: 14,
              paddingHorizontal: 14,
              gap: 8,
            }}
          >
            <SectionTitle level="subsection">
              {t("costs.money_card.scenario.available_in_region_title")}
            </SectionTitle>
            {availableInRegion.map(({ lineId, availability }) => (
              <AvailabilityLine
                key={lineId}
                lineId={lineId}
                availability={availability}
                locale={locale}
                t={t}
              />
            ))}
          </View>
        ) : null}

        {notAvailableInRegion.length > 0 ? (
          <View
            style={{
              borderTopWidth: 1,
              borderTopColor: semanticColors.border.subtle,
              paddingVertical: 14,
              paddingHorizontal: 14,
              gap: 8,
            }}
          >
            <SectionTitle level="subsection">
              {t("costs.money_card.scenario.not_available_in_region_title")}
            </SectionTitle>
            {notAvailableInRegion.map(({ lineId, availability }) => (
              <AvailabilityLine
                key={lineId}
                lineId={lineId}
                availability={availability}
                locale={locale}
                t={t}
              />
            ))}
          </View>
        ) : null}
      </CardSurface>
    );
  };

  const renderEstimateSection = (
    section: EstimateScenarioSection,
    index: number,
  ) => {
    const selectedCount = section.lines.filter((line) =>
      selectedLineKeySet.has(line.key),
    ).length;
    const separateLineCount = section.lines.filter(
      (line) => line.status === "often-separate",
    ).length;
    const sectionTotal = section.lines.reduce(
      (sum, line) =>
        selectedLineKeySet.has(line.key)
          ? sum +
            getLineAmount(line, {
              cycleCount,
              embryoCount,
              storageYears,
              transferCount,
            })
          : sum,
      0,
    );
    const allSelected = selectedCount === section.lines.length;
    const sectionExpanded =
      !isReadOnlyRegion || expandedSectionKeys.has(section.key);

    return (
      <View
        key={section.key}
        style={{
          borderTopWidth: index > 0 ? 1 : 0,
          borderTopColor: semanticColors.border.subtle,
        }}
      >
        <Pressable
          onPress={
            isReadOnlyRegion
              ? () => {
                  capturePress(
                    pressLabel(section.title),
                    "estimate_section_expand",
                  );
                  toggleSectionExpanded(section.key);
                }
              : () => {
                  capturePress(
                    pressLabel(section.title),
                    "estimate_section_row",
                  );
                  toggleSection(section);
                }
          }
          style={{
            paddingHorizontal: 14,
            paddingTop: 13,
            paddingBottom: 10,
            flexDirection: "row",
            alignItems: "flex-start",
            gap: 10,
          }}
        >
          {isReadOnlyRegion ? (
            <View style={{ marginTop: 2 }}>
              <ChevronDown
                size={16}
                color={EMBER.colors.textLight}
                strokeWidth={1.8}
                style={{
                  transform: [{ rotate: sectionExpanded ? "180deg" : "0deg" }],
                }}
              />
            </View>
          ) : (
            <View style={{ marginTop: 1 }}>
              <EstimateStateIcon selected={allSelected} />
            </View>
          )}
          <View style={{ flex: 1, gap: 3 }}>
            <View
              style={{
                flexDirection: "row",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 10,
              }}
            >
              <Text
                className="font-dm-sans"
                style={{
                  flex: 1,
                  fontSize: 13,
                  fontWeight: "700",
                  color: EMBER.colors.text,
                }}
              >
                {section.title}
              </Text>
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                <Text
                  className="font-dm-mono"
                  style={{
                    fontSize: 13,
                    fontWeight: "700",
                    color: EMBER.colors.text,
                    textAlign: "right",
                  }}
                >
                  {formatCurrency(sectionTotal, locale, {
                    currency: estimateCurrency,
                  })}
                </Text>
                {isReadOnlyRegion ? null : (
                  <MoonCheckboxBox checked={allSelected} />
                )}
              </View>
            </View>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                lineHeight: 17,
                color: EMBER.colors.textLight,
              }}
            >
              {section.description}
            </Text>
            {isReadOnlyRegion ? null : (
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 11,
                  color: EMBER.colors.textFaint,
                }}
              >
                {t("costs.money_card.scenario.lines_selected_of_total", {
                  params: {
                    selected: String(selectedCount),
                    total: String(section.lines.length),
                  },
                })}
                {separateLineCount > 0
                  ? ` · ${t(
                      separateLineCount === 1
                        ? "costs.money_card.scenario.usually_separate_lines_one"
                        : "costs.money_card.scenario.usually_separate_lines_other",
                      { params: { count: String(separateLineCount) } },
                    )}`
                  : ""}
              </Text>
            )}
          </View>
        </Pressable>

        <Collapsible expanded={sectionExpanded}>
          <View style={{ paddingBottom: 8 }}>
            {section.lines.map((line, index) => {
              const selected = selectedLineKeySet.has(line.key);
              const statusColor = getStatusColor(line.status);
              return (
                <Pressable
                  key={line.key}
                  onPress={
                    isReadOnlyRegion
                      ? undefined
                      : () => {
                          capturePress(
                            pressLabel(line.label),
                            "estimate_line_row",
                          );
                          toggleLine(line.key);
                        }
                  }
                  style={{
                    marginLeft: 40,
                    marginRight: 12,
                    paddingLeft: 12,
                    paddingRight: 2,
                    paddingVertical: 10,
                    borderLeftWidth: 1,
                    borderLeftColor: semanticColors.border.strong,
                    borderTopWidth: index > 0 ? 1 : 0,
                    borderTopColor: semanticColors.border.subtle,
                    flexDirection: "row",
                    alignItems: "flex-start",
                    gap: 10,
                    opacity: isReadOnlyRegion || selected ? 1 : 0.58,
                  }}
                >
                  <EstimateStateIcon
                    selected={isReadOnlyRegion ? true : selected}
                  />
                  <View style={{ flex: 1, gap: 4 }}>
                    <View
                      style={{
                        flexDirection: "row",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: 10,
                      }}
                    >
                      <Text
                        className="font-dm-sans"
                        style={{
                          flex: 1,
                          fontSize: 12,
                          fontWeight: "600",
                          color: EMBER.colors.text,
                          lineHeight: 17,
                        }}
                      >
                        {line.label}
                      </Text>
                      <View
                        style={{
                          flexDirection: "row",
                          alignItems: "center",
                          gap: 10,
                        }}
                      >
                        <Text
                          className="font-dm-mono"
                          style={{
                            fontSize: 12,
                            fontWeight: "700",
                            color: EMBER.colors.text,
                            textAlign: "right",
                          }}
                        >
                          {formatCurrency(
                            getLineAmount(line, {
                              cycleCount,
                              embryoCount,
                              storageYears,
                              transferCount,
                            }),
                            locale,
                            { currency: estimateCurrency },
                          )}
                        </Text>
                        {isReadOnlyRegion ? null : (
                          <MoonCheckboxBox checked={selected} />
                        )}
                      </View>
                    </View>
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 11,
                        lineHeight: 16,
                        color: EMBER.colors.textLight,
                      }}
                    >
                      {getLineCoverageDetail(line) ?? line.detail}
                    </Text>
                    <Text
                      className="font-dm-sans"
                      style={{
                        alignSelf: "flex-start",
                        fontSize: 10,
                        fontWeight: "700",
                        color: statusColor,
                      }}
                    >
                      {getStatusLabel(line.status, t)}
                    </Text>
                  </View>
                </Pressable>
              );
            })}
          </View>
        </Collapsible>
      </View>
    );
  };

  const renderInsuranceEstimateState = () => (
    <InsightBox
      icon={Shield}
      title={
        hasInsurance
          ? t("costs.money_card.insurance_estimate.insurance_estimate")
          : t("costs.money_card.insurance_estimate.insurance_not_connected")
      }
      body={
        hasInsurance
          ? coverageVerified
            ? t(
                "costs.money_card.insurance_estimate.verified_benefits_apply_line_by_line",
              )
            : t("costs.money_card.insurance_estimate.benefits_not_verified_yet")
          : t("costs.money_card.insurance_estimate.add_insurance_to_estimate")
      }
    />
  );

  const renderEstimateDetails = () => (
    <Collapsible expanded={showBreakdown}>
      <View style={{ paddingTop: 12 }}>{renderScenarioControls()}</View>
    </Collapsible>
  );

  const getQuoteBadgeTone = (
    tone: ClinicQuote["statusTone"],
  ): StatusBadgeTone => {
    if (tone === "ready") return "green";
    if (tone === "attention") return "gold";
    return "neutral";
  };

  const getPaymentToneStyle = (status: PaymentRecord["status"]) => {
    if (status === "paid") {
      return {
        label: t("costs.money_card.payment_status.paid"),
        color: EMBER.colors.green,
        badgeTone: "green" as StatusBadgeTone,
      };
    }
    if (status === "submitted") {
      return {
        label: t("costs.money_card.payment_status.submitted"),
        color: colors.greyBrown,
        badgeTone: "blue" as StatusBadgeTone,
      };
    }
    if (status === "waiting-eob") {
      return {
        label: t("costs.money_card.payment_status.waiting_for_eob"),
        color: colors.greyBrown,
        badgeTone: "neutral" as StatusBadgeTone,
      };
    }
    if (status === "needs-receipt") {
      return {
        label: t("costs.money_card.payment_status.needs_receipt"),
        color: EMBER.colors.gold,
        badgeTone: "gold" as StatusBadgeTone,
      };
    }
    if (status === "partially-covered") {
      return {
        label: t("costs.money_card.payment_status.partially_covered"),
        color: EMBER.colors.gold,
        badgeTone: "gold" as StatusBadgeTone,
      };
    }
    if (status === "ready-reimbursement") {
      return {
        label: t("costs.money_card.payment_status.ready_for_claim"),
        color: colors.greyBrown,
        badgeTone: "green" as StatusBadgeTone,
      };
    }
    if (status === "denied") {
      return {
        label: t("costs.money_card.payment_status.denied"),
        color: EMBER.colors.coral,
        badgeTone: "coral" as StatusBadgeTone,
      };
    }
    if (status === "review") {
      return {
        label: t("costs.money_card.payment_status.needs_match"),
        color: EMBER.colors.gold,
        badgeTone: "gold" as StatusBadgeTone,
      };
    }
    return {
      label: t("costs.money_card.payment_status.left_to_pay"),
      color: EMBER.colors.coral,
      badgeTone: "coral" as StatusBadgeTone,
    };
  };

  const shouldShowPaymentStatusBadge = (status: PaymentRecord["status"]) =>
    status === "submitted" ||
    status === "waiting-eob" ||
    status === "denied" ||
    status === "due" ||
    status === "review";

  const renderStatusBadge = (label: string, tone: StatusBadgeTone) => (
    <View style={{ alignSelf: "flex-start" }}>
      <StatusBadge label={label} tone={tone} />
    </View>
  );

  const renderDocumentNeededBadge = () => (
    <StatusBadge
      label={t("costs.money_card.payments.document_needed")}
      tone="gold"
      icon={FileQuestion}
      iconOnly
      accessibilityLabel={t("costs.money_card.payments.document_needed")}
    />
  );

  const getPaymentRecordIcon = (record: PaymentRecord) => {
    const searchable =
      `${record.label} ${record.source} ${record.phase}`.toLowerCase();

    if (
      searchable.includes("claim") ||
      searchable.includes("insurance") ||
      searchable.includes("eob")
    ) {
      return FileCheck2;
    }

    if (
      searchable.includes("medication") ||
      searchable.includes("pharmacy") ||
      searchable.includes("receipt") ||
      searchable.includes("invoice")
    ) {
      return Receipt;
    }

    if (
      searchable.includes("deposit") ||
      searchable.includes("balance") ||
      record.status === "due"
    ) {
      return Wallet;
    }

    return FileText;
  };

  const renderMiniStat = (label: string, value: string, detail: string) => (
    <View style={{ flex: 1, minWidth: 0, gap: 3 }}>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 10,
          fontWeight: "700",
          color: EMBER.colors.textLight,
        }}
      >
        {label}
      </Text>
      <Text
        className="font-dm-mono"
        style={{ fontSize: 15, fontWeight: "800", color: EMBER.colors.text }}
      >
        {value}
      </Text>
      <Text
        className="font-dm-sans"
        style={{ fontSize: 10, lineHeight: 14, color: EMBER.colors.textFaint }}
      >
        {detail}
      </Text>
    </View>
  );

  const getQuoteConcern = (line: QuoteLineItem) => {
    const detail = line.detail.toLowerCase();
    if (detail.includes("optional") || detail.includes("not included")) {
      return t("costs.money_card.quotes.optional_or_not_included");
    }
    if (detail.includes("separate") || detail.includes("provider")) {
      return t("costs.money_card.quotes.may_be_billed_separately");
    }
    if (detail.includes("confirm") || detail.includes("verify")) {
      return t("costs.money_card.quotes.needs_clinic_confirmation");
    }
    return null;
  };

  const renderQuoteFact = (label: string, value: string, detail: string) => (
    <View style={{ flex: 1, minWidth: 100, gap: 3 }}>
      <Text
        className="font-dm-sans"
        style={{ fontSize: 10, color: EMBER.colors.textLight }}
      >
        {label}
      </Text>
      <Text
        className="font-dm-mono"
        style={{ fontSize: 13, fontWeight: "800", color: EMBER.colors.text }}
      >
        {value}
      </Text>
      <Text
        className="font-dm-sans"
        style={{ fontSize: 10, lineHeight: 14, color: EMBER.colors.textFaint }}
      >
        {detail}
      </Text>
    </View>
  );

  const renderQuoteLineItem = (line: QuoteLineItem) => (
    <View
      key={line.key}
      style={{
        borderTopWidth: 1,
        borderTopColor: semanticColors.border.subtle,
        paddingTop: 11,
        gap: 7,
      }}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 10,
        }}
      >
        <View style={{ flex: 1, minWidth: 0, gap: 3 }}>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              fontWeight: "700",
              color: EMBER.colors.text,
            }}
          >
            {line.label}
          </Text>
          {line.category ? (
            <Text
              className="font-dm-sans"
              style={{ fontSize: 10, color: EMBER.colors.textFaint }}
            >
              {line.category}
            </Text>
          ) : null}
        </View>
        <Text
          className="font-dm-mono"
          style={{
            fontSize: 13,
            fontWeight: "800",
            color: EMBER.colors.text,
          }}
        >
          {formatCurrency(line.amount, locale, {
            currency: estimateCurrency,
          })}
        </Text>
      </View>
      <Text
        className="font-dm-sans"
        style={{ fontSize: 11, lineHeight: 16, color: EMBER.colors.textMid }}
      >
        {getLineCoverageDetail(line) ?? line.detail}
      </Text>
      <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
        <StatusBadge
          label={t("costs.money_card.quotes.from_document")}
          tone="neutral"
        />
        <StatusBadge
          label={
            coverageVerified
              ? t("costs.money_card.quotes.coverage_verified")
              : t("costs.money_card.quotes.coverage_not_checked")
          }
          tone={coverageVerified ? "green" : "gold"}
        />
      </View>
    </View>
  );

  const renderQuoteCard = (quote: ClinicQuote) => {
    const tone = getQuoteBadgeTone(quote.statusTone);
    const hasComparableAmount = quote.statusTone === "ready";
    const devQuoteDeleteFixture =
      __DEV__ &&
      typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).get(
        "dev_costs_quote_delete",
      ) === "1";
    const canDelete =
      clinicQuotesQuery.data?.some(
        (savedQuote) => savedQuote.id === quote.id,
      ) || devQuoteDeleteFixture;
    const quoteConcerns = quote.lineItems.flatMap((line) => {
      const concern = getQuoteConcern(line);
      return concern ? [{ line, concern }] : [];
    });

    return (
      <CardSurface
        key={quote.id}
        variant="panel"
        style={{ overflow: "hidden" }}
      >
        <AccordionItem value={quote.id}>
          <AccordionTrigger
            analyticsLabel={quote.clinic}
            action="clinic_quote_row"
            className="gap-2.5 p-[13px]"
            // The chevron sits under the price, where the card has room.
            chevron={false}
          >
            <View
              style={{
                width: 40,
                height: 40,
                borderRadius: 20,
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "transparent",
                borderWidth: 1,
                borderColor: semanticColors.border.strong,
              }}
            >
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 12,
                  fontWeight: "800",
                  color: colors.greyBrown,
                }}
              >
                {quote.logoText}
              </Text>
            </View>
            <View style={{ flex: 1, minWidth: 0, gap: 4 }}>
              <Text
                className="font-dm-sans"
                numberOfLines={1}
                style={{
                  fontSize: 14,
                  fontWeight: "800",
                  color: EMBER.colors.text,
                }}
              >
                {quote.clinic}
              </Text>
              <Text
                className="font-dm-sans"
                numberOfLines={1}
                style={{ fontSize: 11, color: EMBER.colors.textLight }}
              >
                {quote.location} · {quote.documentLabel}
              </Text>
              <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
                {renderStatusBadge(quote.status, tone)}
                <Text
                  className="font-dm-sans"
                  style={{
                    alignSelf: "center",
                    fontSize: 10,
                    color: EMBER.colors.textFaint,
                  }}
                >
                  {quote.uploadedAt}
                </Text>
              </View>
            </View>
            <View style={{ alignItems: "flex-end", gap: 3 }}>
              <Text
                className="font-dm-mono"
                style={{
                  fontSize: 16,
                  fontWeight: "800",
                  color: EMBER.colors.text,
                }}
              >
                {hasComparableAmount
                  ? formatCurrency(quote.quoteTotal, locale, {
                      currency: estimateCurrency,
                    })
                  : "—"}
              </Text>
              <Text
                className="font-dm-sans"
                style={{ fontSize: 10, color: EMBER.colors.textLight }}
              >
                {hasComparableAmount
                  ? t("costs.money_card.quotes.amount_out_of_pocket", {
                      params: {
                        amount: formatCurrency(quote.outOfPocket, locale, {
                          currency: estimateCurrency,
                        }),
                      },
                    })
                  : t("costs.money_card.quotes.out_of_pocket_unavailable")}
              </Text>
              <AccordionChevron />
            </View>
          </AccordionTrigger>

          <AccordionContent className="pb-0">
            <View
              style={{
                borderTopWidth: 1,
                borderTopColor: semanticColors.border.subtle,
                paddingHorizontal: 13,
                paddingBottom: 13,
                paddingTop: 13,
                gap: 16,
              }}
            >
              {quote.statusTone !== "ready" ? (
                <>
                  <Text
                    className="font-dm-sans"
                    style={{
                      fontSize: 12,
                      lineHeight: 18,
                      color: EMBER.colors.textMid,
                    }}
                  >
                    {quote.summary}
                  </Text>
                  <InsightBox
                    icon={Sparkles}
                    title={quote.analysisTitle}
                    body={quote.analysisBody}
                  />
                </>
              ) : null}

              {quote.statusTone === "attention" ? (
                <CompactActionButton
                  label={t("costs.money_card.quotes.try_reading_again")}
                  action="costs.money_card.quotes.try_reading_again"
                  onPress={() => retryQuoteExtraction(quote.id)}
                  variant="primary"
                  size="sm"
                  style={{ alignSelf: "flex-start" }}
                />
              ) : null}

              {quote.statusTone === "ready" ? (
                <>
                  <View style={{ gap: 9 }}>
                    <View
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 7,
                      }}
                    >
                      <FileText
                        size={14}
                        color={EMBER.colors.textLight}
                        strokeWidth={1.7}
                      />
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 12,
                          fontWeight: "800",
                          color: EMBER.colors.text,
                        }}
                      >
                        {t("costs.money_card.quotes.document_facts")}
                      </Text>
                    </View>
                    <View
                      style={{
                        flexDirection: "row",
                        flexWrap: "wrap",
                        gap: 12,
                      }}
                    >
                      {renderQuoteFact(
                        t("costs.money_card.quotes.quoted_total"),
                        formatCurrency(quote.quoteTotal, locale, {
                          currency: estimateCurrency,
                        }),
                        t("costs.money_card.quotes.from_document"),
                      )}
                      {renderQuoteFact(
                        t("costs.money_card.quotes.quote_date"),
                        quote.quoteDate
                          ? formatDate(quote.quoteDate, locale)
                          : t("costs.money_card.quotes.not_listed"),
                        t("costs.money_card.quotes.from_document"),
                      )}
                      {renderQuoteFact(
                        t("costs.money_card.quotes.patient_estimate"),
                        formatCurrency(quote.outOfPocket, locale, {
                          currency: estimateCurrency,
                        }),
                        !hasInsurance
                          ? t("costs.money_card.quotes.self_pay")
                          : coverageVerified
                            ? t("costs.money_card.quotes.plan_aware_estimate")
                            : t(
                                "costs.money_card.quotes.before_insurance_until_coverage_is_verified",
                              ),
                      )}
                    </View>
                  </View>

                  {quoteConcerns.length > 0 ? (
                    <View style={{ gap: 9 }}>
                      <View
                        style={{
                          flexDirection: "row",
                          alignItems: "center",
                          gap: 7,
                        }}
                      >
                        <FileQuestion
                          size={14}
                          color={EMBER.colors.coral}
                          strokeWidth={1.7}
                        />
                        <Text
                          className="font-dm-sans"
                          style={{
                            fontSize: 12,
                            fontWeight: "800",
                            color: EMBER.colors.text,
                          }}
                        >
                          {t(
                            "costs.money_card.quotes.worth_confirming_with_clinic",
                          )}
                        </Text>
                      </View>
                      {quoteConcerns.map(({ line, concern }) => (
                        <View
                          key={`${quote.id}-${line.key}-concern`}
                          style={{ gap: 3 }}
                        >
                          <Text
                            className="font-dm-sans"
                            style={{
                              fontSize: 11,
                              fontWeight: "800",
                              color: EMBER.colors.text,
                            }}
                          >
                            {line.label} · {concern}
                          </Text>
                          <Text
                            className="font-dm-sans"
                            style={{
                              fontSize: 11,
                              lineHeight: 16,
                              color: EMBER.colors.textMid,
                            }}
                          >
                            {line.detail}
                          </Text>
                        </View>
                      ))}
                    </View>
                  ) : null}

                  <View style={{ gap: 9 }}>
                    <View
                      style={{
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 7,
                      }}
                    >
                      <FileCheck2
                        size={14}
                        color={EMBER.colors.textLight}
                        strokeWidth={1.7}
                      />
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 12,
                          fontWeight: "800",
                          color: EMBER.colors.text,
                        }}
                      >
                        {t("costs.money_card.quotes.line_items_from_document")}
                      </Text>
                    </View>
                    <View style={{ gap: 10 }}>
                      {quote.lineItems.map((line) => renderQuoteLineItem(line))}
                    </View>
                  </View>

                  {!coverageVerified ? (
                    <ActionBanner
                      variant="info"
                      icon={Shield}
                      title={t(
                        "costs.quote_notes.coverage_not_checked_against_quote",
                      )}
                      message={t("costs.quote_notes.estimate_is_modeled")}
                    />
                  ) : null}
                </>
              ) : null}

              {canDelete ? (
                <TextActionButton
                  testID="costs-quote-delete"
                  label={t("costs.money_card.quotes.delete_quote")}
                  action="costs.money_card.quotes.delete_quote"
                  tone="destructive"
                  size="sm"
                  onPress={() => {
                    setQuoteDeleteError(null);
                    setQuoteDeleteTargetId(quote.id);
                  }}
                  disabled={deleteClinicQuoteMutation.isPending}
                  style={{
                    alignSelf: "flex-start",
                    paddingHorizontal: 0,
                    paddingVertical: 4,
                  }}
                />
              ) : null}
            </View>
          </AccordionContent>
        </AccordionItem>
      </CardSurface>
    );
  };

  const renderQuoteDetails = () => {
    const lowestOutOfPocket =
      comparableQuoteCards.length > 0
        ? Math.min(...comparableQuoteCards.map((quote) => quote.outOfPocket))
        : null;
    if (quoteCards.length === 0) {
      return (
        <View style={{ gap: 12 }}>
          <EmptyState
            density="compact"
            titleVariant="featured"
            illustration={
              <DialogIcon
                source={COST_QUOTE_RECEIPT_ILLUSTRATION}
                width={68}
                height={80}
              />
            }
            title={t("costs.money_card.quotes.no_clinic_quotes_yet")}
            subtitle={t(
              "costs.money_card.quotes.upload_quote_to_see_whats_included",
            )}
            action={
              <CompactActionButton
                label={t("costs.money_card.quotes.upload_quote")}
                action="costs.money_card.quotes.upload_quote"
                onPress={() => setUploadModalMode("quote")}
                leadingIcon={UploadCloud}
                variant="primary"
              />
            }
          />{" "}
        </View>
      );
    }

    return (
      <View style={{ gap: 12 }}>
        <View style={{ flexDirection: "row", gap: 16 }}>
          {renderMiniStat(
            t("costs.money_card.quotes.lowest_quote"),
            quoteSummaryAmount != null
              ? formatCurrency(quoteSummaryAmount, locale, {
                  currency: estimateCurrency,
                })
              : "—",
            t("costs.money_card.quotes.before_insurance"),
          )}
          {renderMiniStat(
            t("costs.money_card.quotes.lowest_out_of_pocket"),
            lowestOutOfPocket != null
              ? formatCurrency(lowestOutOfPocket, locale, {
                  currency: estimateCurrency,
                })
              : "—",
            coverageVerified
              ? t("costs.money_card.quotes.plan_aware")
              : t("costs.money_card.quotes.modeled"),
          )}
        </View>

        <Accordion
          type="multiple"
          value={expandedQuoteIds}
          onValueChange={setExpandedQuoteIds}
        >
          <View style={{ gap: 10 }}>{quoteCards.map(renderQuoteCard)}</View>
        </Accordion>
      </View>
    );
  };

  const renderQuoteUploadButton = () => (
    <CompactActionButton
      label={t("costs.money_card.quotes.upload")}
      action="costs.money_card.quotes.upload"
      onPress={() => setUploadModalMode("quote")}
      variant="secondary"
      size="sm"
      leadingIcon={UploadCloud}
      iconStrokeWidth={1.8}
      style={{ position: "absolute", top: 12, right: 18, zIndex: 20 }}
    />
  );

  const renderPaymentRecord = (record: PaymentRecord) => {
    const tone = getPaymentToneStyle(record.status);
    const RecordIcon = getPaymentRecordIcon(record);
    const needsDocument = record.documents.some(
      (document) => document.status === "needed",
    );
    const showDocumentBadge =
      needsDocument || record.status === "needs-receipt";
    const showStatusBadge = shouldShowPaymentStatusBadge(record.status);
    const amountLabel =
      record.billedAmount > 0 || record.status === "due"
        ? formatCurrency(record.amount, locale, { currency: estimateCurrency })
        : t("costs.money_card.payments.amount_pending");

    return (
      <Row
        key={record.id}
        surface="plain"
        leading={
          <RecordIcon
            size={17}
            color={PAYMENT_ROW_ICON_COLOR}
            strokeWidth={1.8}
          />
        }
        leadingSize={28}
        title={record.label}
        subtitle={`${record.provider} · ${record.date}`}
        titleRight={
          <View style={{ flexDirection: "row", alignItems: "center", gap: 5 }}>
            {showStatusBadge
              ? renderStatusBadge(tone.label, tone.badgeTone)
              : null}
            {showDocumentBadge ? renderDocumentNeededBadge() : null}
          </View>
        }
        trailing={
          <Text
            className="font-dm-mono"
            style={{
              fontSize: 13,
              fontWeight: "800",
              color: EMBER.colors.text,
              textAlign: "right",
            }}
          >
            {amountLabel}
          </Text>
        }
        onPress={() => setSelectedPaymentRecordId(record.id)}
        paddingHorizontal={4}
        paddingVertical={12}
        titleStyle={{
          fontSize: 15,
          fontWeight: "600",
          color: EMBER.colors.text,
        }}
        subtitleStyle={{
          fontSize: 12,
          color: EMBER.colors.textLight,
          marginTop: 2,
        }}
      />
    );
  };

  const renderPaymentDetails = () => (
    <View style={{ gap: 12 }}>
      {paymentRecords.length > 0 ? (
        <PaymentProgressBar
          segments={paymentBreakdownSegments}
          leftToPay={leftToPay}
          beforeInsurance={shareBeforeInsurance}
          locale={locale}
          currency={estimateCurrency}
          onPay={() => onNavigate("Financing")}
          onAdd={() => setUploadModalMode("payment")}
        />
      ) : null}

      {paymentRecords.length === 0 ? (
        <EmptyState
          density="compact"
          titleVariant="featured"
          illustration={
            <DialogIcon
              source={COST_QUOTE_RECEIPT_ILLUSTRATION}
              width={68}
              height={80}
            />
          }
          title={t("costs.money_card.payments.no_payments_or_claims_yet")}
          subtitle={t(
            "costs.money_card.payments.add_records_as_treatment_starts",
          )}
          action={
            <CompactActionButton
              label={t("costs.cost_upload.add_payment_or_claim")}
              action="costs.cost_upload.add_payment_or_claim"
              onPress={() => setUploadModalMode("payment")}
              leadingIcon={Plus}
              variant="primary"
            />
          }
        />
      ) : (
        <View style={{ gap: 8 }}>
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              paddingHorizontal: 4,
            }}
          >
            <SectionTitle icon={FileCheck2}>
              {t("costs.money_card.payments.payments_and_claims")}
            </SectionTitle>
            <ViewAllLink onPress={openFullPaymentsList} />
          </View>

          <View>
            {paymentPreviewRecords.map((record, index) => (
              <View
                key={record.id}
                style={{
                  borderTopWidth: index > 0 ? 1 : 0,
                  borderTopColor: semanticColors.border.subtle,
                }}
              >
                {renderPaymentRecord(record)}
              </View>
            ))}
          </View>
        </View>
      )}
    </View>
  );

  const renderPaymentRecordDetailModal = () => {
    if (!selectedPaymentRecord) return null;
    const record = selectedPaymentRecord;

    return (
      <PaymentRecordDetailModal
        record={record}
        locale={locale}
        onClose={() => setSelectedPaymentRecordId(null)}
        onAttachDocument={() => setAttachmentRecordId(record.id)}
        onDeleteDocument={async (documentId) => {
          const rawRecord = (paymentRecordsQuery.data ?? []).find(
            (candidate) => candidate.id === record.id,
          );
          if (!rawRecord) return;
          const documents = detachDocumentFromPaymentRecord(
            rawRecord,
            documentId,
          );
          const updated = await updatePaymentRecordMutation.mutateAsync({
            paymentRecordId: rawRecord.id,
            updates: { documents },
          });
          handlePaymentRecordSaved(apiToUiPaymentRecord(updated, locale));
        }}
        onSaveRecord={handlePaymentRecordSaved}
        onDeleteRecord={() => deletePaymentRecord(record.id)}
        onReviewInsurance={() => {
          setSelectedPaymentRecordId(null);
          onNavigate("Insurance");
        }}
        onOpenFinancing={() => {
          setSelectedPaymentRecordId(null);
          onNavigate("Financing");
        }}
        onOpenTaxTracker={() => {
          setSelectedPaymentRecordId(null);
          onNavigate("TaxReview", { paymentRecordId: record.id });
        }}
        onAskChat={(record) => {
          setSelectedPaymentRecordId(null);
          onOpenChat(record);
        }}
      />
    );
  };

  return (
    <CardSurface
      style={{
        padding: 18,
        gap: 16,
        position: "relative",
        overflow: "visible",
        zIndex: 100,
        elevation: 100,
      }}
    >
      <InfoSheet
        visible={showInfo}
        onClose={() => setShowInfo(false)}
        title={t("costs.money_card.how_it_works.how_this_money_card_works")}
        sections={ESTIMATE_INFO_SECTION_KEYS.map((section) => ({
          title: t(section.titleKey),
          icon: section.icon,
          body: t(section.bodyKey),
        }))}
      />
      <Dialog
        visible={quoteDeleteTarget != null}
        onClose={() => {
          if (!deleteClinicQuoteMutation.isPending) {
            setQuoteDeleteTargetId(null);
            setQuoteDeleteError(null);
          }
        }}
        closeButton="minimal"
      >
        <DialogIcon source={DELETE_CONFIRMATION_ILLUSTRATION} />
        <DialogHeader
          align="center"
          title={t("costs.money_card.quotes.delete_this_quote")}
          subtitle={
            quoteDeleteTarget
              ? t("costs.money_card.quotes.named_quote_will_be_removed", {
                  params: { name: quoteDeleteTarget.name },
                })
              : t("costs.money_card.quotes.quote_will_be_removed")
          }
          style={{ marginTop: 16 }}
        />
        {quoteDeleteError ? (
          <ActionBanner
            variant="important"
            message={quoteDeleteError}
            style={{ marginTop: 14 }}
          />
        ) : null}
        <DialogActions style={{ marginTop: 18 }}>
          <SecondaryButton
            label={t("costs.money_card.quotes.cancel_deleting")}
            action="costs.money_card.quotes.cancel_deleting"
            onPress={() => {
              setQuoteDeleteTargetId(null);
              setQuoteDeleteError(null);
            }}
            disabled={deleteClinicQuoteMutation.isPending}
            flex={1}
          />
          <SecondaryButton
            variant="destructive"
            label={
              deleteClinicQuoteMutation.isPending
                ? t("costs.money_card.quotes.deleting")
                : t("common.actions.delete")
            }
            action="costs.money_card.quotes.delete_quote"
            onPress={deleteQuote}
            loading={deleteClinicQuoteMutation.isPending}
            flex={1}
          />
        </DialogActions>
      </Dialog>
      <CostDocumentUploadModal
        visible={uploadModalMode != null}
        mode={uploadModalMode ?? "quote"}
        onClose={() => setUploadModalMode(null)}
        onDocumentPicked={handleCostDocumentPicked}
        onManualPaymentAdded={handleManualPaymentAdded}
      />
      <CostDocumentUploadModal
        visible={attachmentRecordId != null}
        mode="payment"
        documentOnly
        onClose={() => setAttachmentRecordId(null)}
        onDocumentPicked={attachPaymentDocument}
        onManualPaymentAdded={async () => undefined}
      />
      {renderPaymentRecordDetailModal()}
      {state === "clinic-estimate" && quoteCards.length > 0
        ? renderQuoteUploadButton()
        : null}

      {actionButtonStyle === "care" && state === "estimate" ? (
        <View style={{ zIndex: 30 }}>
          {renderAssumptions({ compact: true })}
        </View>
      ) : hideInternalStateSwitch ? null : (
        <StateSwitch active={state} onChange={onStateChange} />
      )}

      {actionButtonStyle === "care" && state === "estimate" ? (
        <View
          style={{
            alignItems: "center",
            marginTop: 8,
            marginBottom: -6,
          }}
        >
          <Image
            source={COST_ESTIMATE_ILLUSTRATION}
            resizeMode="contain"
            style={{
              width: 76,
              height: 64,
              opacity: 0.82,
            }}
          />
        </View>
      ) : null}

      {state === "clinic-estimate" && quoteCards.length > 0 ? (
        <View
          style={{
            alignItems: "center",
            marginTop: 2,
            marginBottom: -2,
          }}
        >
          <Image
            source={COST_QUOTE_RECEIPT_ILLUSTRATION}
            resizeMode="contain"
            style={{ width: 82, height: 68, opacity: 0.82 }}
          />
        </View>
      ) : null}

      {showHeroSummary ? (
        <View
          style={{
            flexDirection: actionButtonStyle === "care" ? "column" : "row",
            flexWrap: actionButtonStyle === "care" ? "nowrap" : "wrap",
            alignItems: "flex-start",
            justifyContent: "space-between",
            columnGap: 18,
            rowGap: 12,
            zIndex: 10,
          }}
        >
          <View
            style={{
              flex: 1,
              minWidth: 180,
              alignItems:
                actionButtonStyle === "care" ? "center" : "flex-start",
              alignSelf: actionButtonStyle === "care" ? "stretch" : undefined,
              gap: 6,
            }}
          >
            <View
              testID="cost-estimate-total"
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent:
                  actionButtonStyle === "care" ? "center" : "flex-start",
                gap: 6,
              }}
            >
              <AnimatedCurrencyAmount
                value={summaryAmountValue}
                locale={locale}
                currency={estimateCurrency}
                color={
                  actionButtonStyle === "care"
                    ? colors.greyBrown
                    : EMBER.colors.text
                }
              />
              <InfoButton onPress={() => setShowInfo(true)} />
            </View>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 12,
                color: EMBER.colors.textLight,
                textAlign: actionButtonStyle === "care" ? "center" : "left",
              }}
            >
              {summary.amountLabel}
            </Text>
            {actionButtonStyle === "care" && !showBreakdown && cardNarrative ? (
              <Text
                className="font-dm-sans"
                style={{
                  maxWidth: 360,
                  fontSize: 12,
                  lineHeight: 18,
                  color: EMBER.colors.textMid,
                  textAlign: "center",
                }}
              >
                {cardNarrative}
              </Text>
            ) : null}
          </View>
          {state === "estimate" && actionButtonStyle !== "care" ? (
            <View
              style={{
                alignItems: "flex-end",
                flexShrink: 1,
                minWidth: 0,
                paddingTop: 1,
              }}
            >
              {renderAssumptions()}
            </View>
          ) : null}
        </View>
      ) : null}

      {state === "estimate" && locationNoticeMessage ? (
        <ActionBanner
          variant="info"
          icon={MapPin}
          message={locationNoticeMessage}
        />
      ) : null}

      {state === "estimate" ? renderEstimateDetails() : null}
      {state === "estimate" && !showBreakdown
        ? renderPublicCoverageSummary()
        : null}
      {state === "clinic-estimate" ? renderQuoteDetails() : null}
      {state === "payments" ? renderPaymentDetails() : null}

      {financialPlanLabel ? (
        <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
          <CheckCircle2
            size={14}
            color={EMBER.colors.green}
            strokeWidth={1.8}
          />
          <Text
            className="font-dm-sans"
            style={{ flex: 1, fontSize: 12, color: EMBER.colors.textLight }}
          >
            {financialPlanLabel}
          </Text>
        </View>
      ) : null}

      {state === "clinic-estimate" &&
      quoteCards.length === 0 ? null : actionButtonStyle === "care" ? (
        <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
          {summary.secondaryRoute ? (
            <CompactActionButton
              label={summary.secondaryAction}
              action={summary.secondaryActionKey}
              leadingIcon={SecondaryActionIcon}
              variant="secondary"
              flexGrow={1}
              minWidth={0}
              onPress={() => onNavigate(summary.secondaryRoute!)}
            />
          ) : null}
          <CompactActionButton
            label={summary.primaryAction}
            action={summary.primaryActionKey}
            leadingIcon={PrimaryActionIcon}
            variant="primary"
            flexGrow={1}
            minWidth={0}
            onPress={runPrimaryAction}
          />
        </View>
      ) : (
        <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 10 }}>
          <Pressable
            onPress={() => {
              capturePress(
                pressLabel(summary.primaryAction),
                summary.primaryActionKey,
              );
              runPrimaryAction();
            }}
            style={{
              flex: 1,
              minWidth: 190,
              minHeight: 40,
              borderRadius: 14,
              backgroundColor: "#d6cbc3",
              alignItems: "center",
              justifyContent: "center",
              paddingHorizontal: 14,
              flexDirection: "row",
              gap: 7,
            }}
          >
            <PrimaryActionIcon
              size={15}
              color={EMBER.colors.text}
              strokeWidth={1.8}
            />
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 13,
                fontWeight: "700",
                color: EMBER.colors.text,
                textAlign: "center",
              }}
            >
              {summary.primaryAction}
            </Text>
          </Pressable>
          {summary.secondaryRoute ? (
            <Pressable
              onPress={() => {
                capturePress(
                  pressLabel(summary.secondaryAction),
                  summary.secondaryActionKey,
                );
                onNavigate(summary.secondaryRoute!);
              }}
              style={{
                flex: 1,
                minWidth: 190,
                minHeight: 40,
                borderRadius: 14,
                borderWidth: 1,
                borderColor: semanticColors.border.strong,
                backgroundColor: semanticColors.surface.glass,
                alignItems: "center",
                justifyContent: "center",
                paddingHorizontal: 14,
                flexDirection: "row",
                gap: 7,
              }}
            >
              <SecondaryActionIcon
                size={15}
                color={EMBER.colors.textMid}
                strokeWidth={1.8}
              />
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 13,
                  fontWeight: "600",
                  color: EMBER.colors.textMid,
                  textAlign: "center",
                }}
              >
                {summary.secondaryAction}
              </Text>
            </Pressable>
          ) : null}
        </View>
      )}
    </CardSurface>
  );
}

export default function CostsOverviewConceptScreen({
  navigation,
  route,
}: Props) {
  const rootNavigation =
    navigation.getParent<NavigationProp<MainTabParamList>>();
  const region = useRegion();
  const { locale } = useLocale();
  const t = useTranslations();
  const capturePress = usePressCapture("CostsOverviewConceptScreen");
  const { userId } = useApiClient();
  const { data: onboardingData } = useOnboarding();
  const { data: patient } = usePatient();
  const authorizedExperience = useAuthorizedExperienceProjection();
  const canonicalExperience = authorizedExperience.projection?.context;
  const acceptedPlan = authorizedExperience.canonical?.acceptedTreatmentPlan;
  const {
    primaryPolicy,
    hasInsurance,
    verificationStatus,
    fertilityCoverage,
    isLoading: coverageLoading,
  } = useCoverage();
  const financials = useCoverageFinancials();
  const comparison = useClinicComparisonStore(userId);
  const [insuranceModalVisible, setInsuranceModalVisible] = useState(false);
  const [reviewStateControlBar, setReviewStateControlBar] =
    useState<ConceptCostState | null>(
      route.params?.initialState ?? getUrlCostState(),
    );
  const requestedInitialState = route.params?.initialState;
  const requestedUploadMode = route.params?.openUploadMode;

  useEffect(() => {
    if (!requestedInitialState) return;
    setReviewStateControlBar(requestedInitialState);
    navigation.setParams({ initialState: undefined });
  }, [navigation, requestedInitialState]);

  const consumeUploadMode = useCallback(() => {
    navigation.setParams({ openUploadMode: undefined });
  }, [navigation]);

  const treatmentIntent: TreatmentIntent | null = canonicalExperience?.treatment
    .hasActiveCycle
    ? "InTreatment"
    : null;
  const hasPartner =
    canonicalExperience?.familyBuildingPlan.partnerChoice === "with-partner";
  const isUsingDonor =
    canonicalExperience?.familyBuildingPlan.partnerChoice === "using-donor";
  const plannedModality = resolvePlannedModality(
    normalizePlannedModalities(
      authorizedExperience.canonical?.intent.plannedModalities,
    ),
  );
  const acceptedPlanTreatmentType = getAcceptedPlanTreatmentType(
    acceptedPlan?.treatmentFamily,
  );
  const canonicalTreatmentType =
    authorizedExperience.status === "ready"
      ? (acceptedPlanTreatmentType ??
        getCanonicalTreatmentType(
          canonicalExperience?.familyBuildingPlan.treatmentGoal,
          plannedModality,
        ))
      : null;
  // Keep the estimate and its Scenario Builder available while context loads
  // or remains exploratory. A canonical treatment path remains the initial
  // selection; the fallback keeps the existing estimate surface interactive
  // while the person chooses a path.
  const initialTreatmentType = canonicalTreatmentType ?? "ivf";
  const profileAddress = patient?.addresses?.[0];
  const profileCountryCode =
    profileAddress?.country === "USA"
      ? "US"
      : (profileAddress?.country ?? "US");
  const profileZipCode = profileAddress?.postalCode ?? onboardingData.zipCode;
  const profileCity = profileAddress?.city ?? null;
  // The onboarding state derivation is intentionally not persisted by the
  // current address contract. Resolve it from the canonical postal code so
  // Costs does not depend on cleared onboarding context after reload.
  const profileState = inferUsStateCodeFromZip(profileZipCode);
  const diagnosticProfile: DiagnosticProfile =
    canonicalExperience?.viewer.sexAtBirth === "AMAB" ||
    canonicalTreatmentType === "sperm_freezing"
      ? "male"
      : hasPartner
        ? "couple"
        : "female";

  const verifiedClinicCount = Object.values(
    comparison.verificationStatuses,
  ).filter((status) => status === "verified").length;
  const derivedState = deriveConceptCostState({
    intent: treatmentIntent,
    comparisonCount: comparison.comparisonClinicIds.length,
    verifiedClinicCount,
  });
  const controlBarActiveState = reviewStateControlBar ?? derivedState;

  const payerName =
    primaryPolicy?.payer?.displayName ??
    primaryPolicy?.insuranceCarrier ??
    financials.carrierName;
  const insuranceLabel = !hasInsurance
    ? "Not added"
    : verificationStatus === "verified"
      ? `${payerName ?? "Insurance"} verified`
      : verificationStatus === "pending"
        ? `${payerName ?? "Insurance"} pending`
        : (payerName ?? "Insurance added");
  const financialPlanLabel =
    financials.lifetimeMax || financials.annualMax
      ? t("costs.money_card.known_benefit_limit", {
          params: {
            limit: String(financials.lifetimeMax ?? financials.annualMax),
          },
        })
      : financials.outOfPocketMax
        ? t("costs.money_card.known_out_of_pocket_max", {
            params: { amount: String(financials.outOfPocketMax) },
          })
        : null;

  // AdaptiveCostStatusCard now renders for any region whose catalog we've
  // seeded (US and FR today). France uses mostly €0 funded lines with
  // Assurance Maladie coverage detail and a "Not available" section for
  // PGT-A, PGT-P, known-donor coordination.
  const isSupportedRegion = region === "US" || region === "FR";
  const isUS = region === "US";
  const comingSoon = t("costs.coming_soon");
  const currentFinancingProviderCount = currentFinancingProviders().length;
  const financingProviderCountForm =
    currentFinancingProviderCount === 1 ? "one" : "other";
  const financingSubtitle =
    currentFinancingProviderCount > 0
      ? t(
          hasCurrentPromotionalFinancing()
            ? `costs.ways_to_pay.providers_with_promotional_options_${financingProviderCountForm}`
            : `costs.ways_to_pay.providers_with_varying_terms_${financingProviderCountForm}`,
          { params: { count: String(currentFinancingProviderCount) } },
        )
      : t("costs.ways_to_pay.provider_details_unavailable");
  const reportedEmployerBenefit =
    patient?.fertilityIntent?.employerBenefit ?? onboardingData.employerBenefit;
  const insuranceSummary = !hasInsurance
    ? t("costs.ways_to_pay.add_insurance_to_see_coverage")
    : verificationStatus === "verified"
      ? t("costs.ways_to_pay.insurer_verified", {
          params: {
            insurer:
              primaryPolicy?.payer?.displayName ??
              primaryPolicy?.insuranceCarrier ??
              t("costs.insurance"),
          },
        })
      : verificationStatus === "pending"
        ? t("costs.ways_to_pay.insurer_verification_pending", {
            params: {
              insurer: primaryPolicy?.insuranceCarrier ?? t("costs.insurance"),
            },
          })
        : (primaryPolicy?.insuranceCarrier ?? t("costs.insurance"));

  const categories: CategoryCard[] = [
    {
      title: t("costs.insurance"),
      subtitle: isUS ? insuranceSummary : comingSoon,
      icon: Shield,
      iconVariant: "mahogany",
      route: "Insurance",
      action: "costs.insurance",
    },
    {
      title: t("costs.employer_benefits"),
      subtitle: isUS
        ? reportedEmployerBenefit
          ? reportedEmployerBenefit
          : t("costs.ways_to_pay.employer_programs_hsa_fsa_state_mandates")
        : comingSoon,
      icon: Briefcase,
      iconVariant: "copper",
      route: "EmployerBenefits",
      action: "costs.employer_benefits",
    },
    {
      title: t("costs.financing"),
      subtitle: isUS ? financingSubtitle : comingSoon,
      icon: CreditCard,
      iconVariant: "amber",
      route: "Financing",
      action: "costs.financing",
    },
    {
      title: t("costs.grants"),
      subtitle: isUS
        ? t("costs.ways_to_pay.grant_programs_available", {
            params: { count: String(GRANTS.length) },
          })
        : comingSoon,
      icon: Gift,
      iconVariant: "bronze",
      route: "Grants",
      action: "costs.grants",
    },
    {
      title: t("costs.tax_deductions"),
      subtitle: isUS
        ? t("costs.ways_to_pay.fertility_expenses_may_be_deductible")
        : comingSoon,
      icon: Receipt,
      iconVariant: "ember",
      route: "TaxDeductions",
      action: "costs.tax_deductions",
    },
  ];

  const openInsuranceModal = () => setInsuranceModalVisible(true);
  const navigateToRoute = (
    route: CostsOverviewRouteName,
    params?: CostsOverviewRouteParams,
  ) => {
    switch (route) {
      case "Insurance":
        navigation.navigate("Insurance");
        break;
      case "EmployerBenefits":
        navigation.navigate("EmployerBenefits");
        break;
      case "Financing":
        navigation.navigate("Financing");
        break;
      case "Grants":
        navigation.navigate("Grants");
        break;
      case "Payments":
        navigation.navigate("Payments");
        break;
      case "TaxDeductions":
        navigation.navigate("TaxDeductions");
        break;
      case "TaxReview":
        navigation.navigate("TaxReview", params);
        break;
      case "Glossary":
        navigation.navigate("Glossary");
        break;
    }
  };

  return (
    <GlassScreenLayout
      overlay={
        <InsuranceInfoModal
          visible={insuranceModalVisible}
          onClose={() => setInsuranceModalVisible(false)}
          primaryPolicy={primaryPolicy}
          hasInsurance={hasInsurance}
          verificationStatus={verificationStatus}
          fertilityCoverage={fertilityCoverage}
          isLoading={coverageLoading}
        />
      }
    >
      <ScrollView
        contentContainerStyle={{
          paddingHorizontal: 16,
          paddingTop: SCREEN_PADDING_TOP || HEADER_INSET,
          paddingBottom: 100,
          maxWidth: 520,
          width: "100%",
          alignSelf: "center",
        }}
        showsVerticalScrollIndicator={false}
      >
        <ScreenHeader title={t("costs.costs")} />
        {isSupportedRegion ? (
          <View
            style={{
              zIndex: 100,
              elevation: 100,
              overflow: "visible",
              gap: 16,
            }}
          >
            <View style={{ zIndex: 300, elevation: 300, overflow: "visible" }}>
              <PhysicalStateTabs
                active={controlBarActiveState}
                onChange={setReviewStateControlBar}
              />
              <AdaptiveCostStatusCard
                state={controlBarActiveState}
                onStateChange={setReviewStateControlBar}
                initialTreatmentType={initialTreatmentType}
                diagnosticProfile={diagnosticProfile}
                locale={locale}
                profileCity={profileCity}
                profileState={profileState}
                profileZipCode={profileZipCode}
                profileCountryCode={profileCountryCode}
                hasInsurance={hasInsurance}
                coverageVerified={verificationStatus === "verified"}
                verificationStatus={verificationStatus}
                insuranceLabel={insuranceLabel}
                fertilityCoverage={fertilityCoverage}
                primaryPolicy={primaryPolicy ?? null}
                clinicCount={comparison.comparisonClinicIds.length}
                verifiedClinicCount={verifiedClinicCount}
                financialPlanLabel={financialPlanLabel}
                onAddInsurance={openInsuranceModal}
                onNavigate={navigateToRoute}
                onViewAllPayments={(params) =>
                  navigation.navigate("Payments", params)
                }
                onOpenChat={(record) => {
                  rootNavigation?.navigate("Chat", {
                    screen: "Assistant",
                    params: {
                      initialPrompt: buildPaymentRecordChatPrompt(
                        record,
                        locale,
                      ),
                      autoSendInitialPrompt: true,
                    },
                  });
                }}
                onCompareQuotes={(context) => {
                  rootNavigation?.navigate("Chat", {
                    screen: "Assistant",
                    params: {
                      initialPrompt: buildQuoteComparisonChatPrompt(
                        context,
                        locale,
                        t,
                      ),
                      autoSendInitialPrompt: true,
                    },
                  });
                }}
                initialUploadMode={requestedUploadMode}
                onUploadModeConsumed={consumeUploadMode}
                actionButtonStyle="care"
                topControlsVariant="bar"
                hideInternalStateSwitch
              />
            </View>
          </View>
        ) : (
          <CardSurface style={{ padding: 20, gap: 12 }}>
            <Building2
              size={20}
              color={EMBER.colors.textLight}
              strokeWidth={1.5}
            />
            <View>
              <CardTitleText>{t("costs.us_first_title")}</CardTitleText>
              <CardSubtitleText>{t("costs.us_first_body")}</CardSubtitleText>
            </View>
          </CardSurface>
        )}

        {isUsingDonor && (
          <CardSurface style={{ marginTop: 12, padding: 14 }}>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 13,
                color: EMBER.colors.textMid,
                lineHeight: 19,
              }}
            >
              {t("costs.donor_scenario_note")}
            </Text>
          </CardSurface>
        )}

        <View style={{ marginTop: 24, marginBottom: 12, paddingHorizontal: 2 }}>
          <SectionTitle icon={Wallet}>
            {t("costs.coverage_and_ways_to_pay")}
          </SectionTitle>
        </View>

        <View style={{ gap: 12 }}>
          {categories.map((cat) => {
            const isCategoryComing = cat.subtitle === comingSoon;
            return (
              <Pressable
                key={cat.route}
                onPress={
                  isCategoryComing
                    ? undefined
                    : () => {
                        // The subtitle carries the person's carrier and plan
                        // details, so only the category title is captured.
                        capturePress(pressLabel(cat.title), cat.action);
                        navigateToRoute(cat.route);
                      }
                }
                style={{
                  ...cardSurface(),
                  padding: 18,
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 14,
                  opacity: isCategoryComing ? 0.5 : 1,
                }}
              >
                <GradientIcon
                  icon={cat.icon}
                  variant={cat.iconVariant}
                  size={24}
                  strokeWidth={1.6}
                />
                <View style={{ flex: 1 }}>
                  <CardTitleText>{cat.title}</CardTitleText>
                  <CardSubtitleText>{cat.subtitle}</CardSubtitleText>
                </View>
                <ChevronRight
                  size={16}
                  color={EMBER.colors.textFaint}
                  strokeWidth={1.5}
                />
              </Pressable>
            );
          })}
        </View>

        <View style={{ marginTop: 16 }}>
          <CompareClinicCostsCard />
        </View>

        <RegionGate regions={["US"]}>
          <View style={{ marginTop: 24 }}>
            <InsuranceGlossaryCard
              onPress={() => navigation.navigate("Glossary")}
            />
          </View>
        </RegionGate>

        <FaqSection style={{ marginTop: 24 }} />

        <View style={{ marginTop: 16 }}>
          <ChatWithUsCard />
        </View>
      </ScrollView>
    </GlassScreenLayout>
  );
}
